import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { lighten } from "@mui/material/styles";
import {
  AddRounded as AddRoundedIcon,
  DeleteRounded as DeleteRoundedIcon,
  ClearRounded as ClearRoundedIcon,
  DragIndicatorRounded as DragIndicatorRoundedIcon,
  RestartAltRounded as RestartAltRoundedIcon,
} from "@mui/icons-material";
import { useShareAction } from "../../contexts/ShareActionContext";
import PageShareModal from "../common/PageShareModal";
import { settingsService } from "../../services/settings/settingsService";

type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

type CategoryId = string;

type TimeBlock = {
  start: string; // HH:MM
  end: string; // HH:MM
};

type BlocksByDay = Partial<Record<DayKey, TimeBlock[]>>;

type CategoryDef = {
  id: CategoryId;
  label: string;
  subtitle?: string;
  baseRate: number;
  hoursByDay: Record<DayKey, number>;
  fixedBlocksByDay?: Partial<Record<DayKey, TimeBlock[]>>;
};

type SpouseValueSettingsV3 = {
  version: 3;
  childrenCount: number;
  homeschoolExtraPerChild: number;
  wifeCategories: CategoryDef[];
  husbandCategories: CategoryDef[];
  eliteBlocksByDay?: BlocksByDay;
  showEliteEscortJoke: boolean;
  showElitePremium: boolean;
  wifeMeterState?: MeterStateV1;
  husbandMeterState?: MeterStateV1;
};

type MeterKind = "masculinity" | "femininity";
type MeterSection = "Morning" | "Day" | "Evening" | "Night";

type MeterItem = {
  id: string;
  label: string;
  points: number;
};

type MeterStateV1 = {
  version: 1;
  items: Record<MeterKind, Record<MeterSection, MeterItem[]>>;
  checked: Record<string, boolean>;
};

const days: Array<{ key: DayKey; label: string }> = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

const emptyHoursByDay = (): Record<DayKey, number> => ({
  mon: 0,
  tue: 0,
  wed: 0,
  thu: 0,
  fri: 0,
  sat: 0,
  sun: 0,
});

const clampNumber = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const parseTimeToMinutes = (hhmm: string): number | null => {
  const match = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
};

const normalizeHHMM = (value: unknown, fallback: string) => {
  const s = typeof value === "string" ? value : "";
  const m = parseTimeToMinutes(s);
  if (m == null) return fallback;
  return minutesToHHMM(m);
};

const minutesToHHMM = (minutes: number) => {
  const m = Math.max(0, Math.min(23 * 60 + 59, Math.round(minutes)));
  const hh = String(Math.floor(m / 60)).padStart(2, "0");
  const mm = String(m % 60).padStart(2, "0");
  return `${hh}:${mm}`;
};

const formatMinutesToParts = (minutes: number) => {
  const m = Math.max(0, Math.round(minutes));
  const h24 = Math.floor(m / 60) % 24;
  const mm = String(m % 60).padStart(2, "0");
  const suffix = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return { time: `${h12}:${mm}`, suffix };
};

const TimeLabel: React.FC<{ minutes: number }> = ({ minutes }) => {
  const { time, suffix } = formatMinutesToParts(minutes);
  return (
    <>
      <Box component="span">{time}</Box>
      <Box
        component="sup"
        sx={{ ml: 0.25, fontSize: "0.68em", fontWeight: 900 }}
      >
        {suffix}
      </Box>
    </>
  );
};

const computeHoursFromBlocks = (blocks: TimeBlock[]) => {
  return blocks.reduce((sum, b) => {
    const s = parseTimeToMinutes(b.start);
    const e = parseTimeToMinutes(b.end);
    if (s == null || e == null) return sum;
    if (e <= s) return sum;
    return sum + (e - s) / 60;
  }, 0);
};

const formatHours = (value: number) => {
  const rounded = Math.round(value * 10) / 10;
  return `${rounded.toFixed(1)} hrs`;
};

const formatHoursNumber = (value: number) => {
  const rounded = Math.round(value * 10) / 10;
  return rounded.toFixed(1);
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value);

const createId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const palette = [
  "#14B8A6",
  "#6366F1",
  "#22C55E",
  "#F97316",
  "#F59E0B",
  "#A855F7",
  "#EC4899",
  "#06B6D4",
  "#84CC16",
  "#EF4444",
] as const;

const hashString = (value: string) => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

const getCategoryColor = (id: string) => {
  const idx = hashString(id) % palette.length;
  return palette[idx];
};

const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 48);

const buildDefaultEliteBlocksByDay = (): BlocksByDay => ({
  mon: [{ start: "07:30", end: "08:00" }],
  tue: [{ start: "21:00", end: "21:45" }],
  fri: [{ start: "21:00", end: "21:45" }],
  sat: [{ start: "21:00", end: "21:45" }],
});

const meterSections: MeterSection[] = ["Morning", "Day", "Evening", "Night"];

const buildDefaultMeterState = (who: "wife" | "husband"): MeterStateV1 => {
  const mk = (label: string, points: number): MeterItem => ({
    id: createId(),
    label,
    points,
  });

  const husbandMasculinity: Record<MeterSection, MeterItem[]> = {
    Morning: [
      mk("Give wife a kiss before leaving.", 1),
      mk("Give wife a life-giving note.", 1),
      mk("Give wife a hug before leaving.", 1),
      mk("Share the plan for the day (quick clarity).", 1),
      mk("Initiate prayer / gratitude together.", 1),
    ],
    Day: [
      mk("Text her encouragement mid-day.", 1),
      mk("Handle a hard task she is carrying.", 2),
      mk("Send a family photo/video to reinforce vision.", 1),
      mk("Confirm tonight’s connection plan.", 1),
      mk("Protect time boundaries (say no to distractions).", 2),
    ],
    Evening: [
      mk("Greet her with eye contact and warmth.", 1),
      mk("Take over a kid task without being asked.", 2),
      mk("Sit with her for 10 minutes with no phone.", 2),
      mk("Lead a quick family reset (tone, order, peace).", 1),
      mk("Invite partnership: “How can I help tonight?”", 1),
    ],
    Night: [
      mk("Initiate a short debrief: highs/lows of the day.", 1),
      mk("Offer affection without expectation.", 1),
      mk("Create a calm environment (lights, tidy, quiet).", 1),
      mk("Ask what would make her feel cared for.", 2),
      mk("Initiate intimacy with kindness and patience.", 2),
    ],
  };

  const wifeFemininity: Record<MeterSection, MeterItem[]> = {
    Morning: [
      mk("Give husband a kiss before leaving.", 1),
      mk("Give husband a life-giving note.", 1),
      mk("Give husband a hug before leaving.", 1),
      mk("Tell him one thing you appreciate about him.", 1),
      mk("Send him off with warmth and confidence.", 1),
    ],
    Day: [
      mk("Remind him you want connection tonight.", 1),
      mk("Share a family photo/video to reinforce vision.", 1),
      mk("Handle one admin task to reduce mental load.", 1),
      mk("Keep the home energy peaceful (where possible).", 1),
      mk("Encourage him with a short message.", 1),
    ],
    Evening: [
      mk("Greet husband at the door.", 1),
      mk("Beat the kids to greet him first.", 2),
      mk("Give him a kiss when he gets home.", 1),
      mk("Give him a hug when he gets home.", 1),
      mk("Have a meal ready (or a plan for it).", 1),
    ],
    Night: [
      mk("Cuddle up with him.", 1),
      mk("Turn around to cuddle (extra intentional).", 1),
      mk("Invite connection: “How are you really?”", 1),
      mk("Initiate intimacy in a way that blesses him.", 2),
      mk("Share your vision for your life together.", 1),
    ],
  };

  const neutral: Record<MeterSection, MeterItem[]> = {
    Morning: [
      mk("Offer a kind greeting to start the day.", 1),
      mk("Do one task you’ve been putting off.", 1),
      mk("Encourage your spouse in one sentence.", 1),
      mk("Share a quick plan for connection.", 1),
      mk("Take care of your body (movement / water).", 1),
    ],
    Day: [
      mk("Send one supportive message.", 1),
      mk("Remove one stressor for your spouse.", 2),
      mk("Do one act of service.", 1),
      mk("Protect a boundary that matters.", 1),
      mk("Plan one meaningful moment tonight.", 1),
    ],
    Evening: [
      mk("Welcome your spouse warmly.", 1),
      mk("Do one household task proactively.", 1),
      mk("Be present for 10 minutes (no phone).", 2),
      mk("Offer a hug and eye contact.", 1),
      mk("Speak one appreciation out loud.", 1),
    ],
    Night: [
      mk("Do a short debrief together.", 1),
      mk("Offer affection without expectations.", 1),
      mk("Set tomorrow up for peace.", 1),
      mk("Ask for what you need with respect.", 1),
      mk("End the day with gratitude.", 1),
    ],
  };

  const items: MeterStateV1["items"] =
    who === "wife"
      ? { masculinity: neutral, femininity: wifeFemininity }
      : { masculinity: husbandMasculinity, femininity: neutral };

  return { version: 1, items, checked: {} };
};

const calcMeterTotals = (state: MeterStateV1, kind: MeterKind) => {
  const all = meterSections.flatMap((s) => state.items[kind][s]);
  const max = all.reduce((sum, i) => sum + (Number(i.points) || 0), 0);
  const earned = all.reduce(
    (sum, i) => sum + (state.checked[i.id] ? Number(i.points) || 0 : 0),
    0
  );
  const pct = max > 0 ? Math.min(1, Math.max(0, earned / max)) : 0;
  const tier = pct < 0.34 ? "Low" : pct < 0.67 ? "Medium" : "High";
  const bonusPct = tier === "Medium" ? 100 : tier === "High" ? 300 : 0;
  const multiplier = 1 + bonusPct / 100;
  return { earned, max, pct, tier, bonusPct, multiplier };
};

const spouseMeterKey = (who: "wife" | "husband") =>
  `spouse_value_meter_v1:${who}`;

const loadMeterState = (who: "wife" | "husband"): MeterStateV1 => {
  try {
    const raw = localStorage.getItem(spouseMeterKey(who));
    if (!raw) return buildDefaultMeterState(who);
    const parsed = JSON.parse(raw) as Partial<MeterStateV1>;
    if (parsed.version !== 1 || !parsed.items || !parsed.checked) {
      return buildDefaultMeterState(who);
    }
    return parsed as MeterStateV1;
  } catch {
    return buildDefaultMeterState(who);
  }
};

const saveMeterState = (who: "wife" | "husband", state: MeterStateV1) => {
  try {
    localStorage.setItem(spouseMeterKey(who), JSON.stringify(state));
  } catch {
    // ignore
  }
};

const shiftMondayToSaturday = (categories: CategoryDef[]) => {
  // Some roles are intentionally anchored to Monday.
  const exceptions = new Set([
    "house-cleaner",
    "cook",
    "chef",
    "human-resources",
  ]);
  return categories.map((c) => {
    if (exceptions.has(c.id)) return c;

    const nextHours: Record<DayKey, number> = { ...c.hoursByDay };
    const monHours = Number(nextHours.mon || 0);
    if (monHours > 0) {
      nextHours.sat = Number(nextHours.sat || 0) + monHours;
      nextHours.mon = 0;
    }

    const monBlocks = c.fixedBlocksByDay?.mon || [];
    if (monBlocks.length) {
      const satBlocks = c.fixedBlocksByDay?.sat || [];
      const nextFixed: CategoryDef["fixedBlocksByDay"] = {
        ...(c.fixedBlocksByDay || {}),
        sat: [...satBlocks, ...monBlocks],
      };
      delete (nextFixed as any).mon;

      // Keep hoursByDay consistent when fixed blocks are present.
      nextHours.mon = 0;
      nextHours.sat = computeHoursFromBlocks(nextFixed.sat || []);

      const fixed = Object.keys(nextFixed).length > 0 ? nextFixed : undefined;
      return { ...c, hoursByDay: nextHours, fixedBlocksByDay: fixed };
    }

    return { ...c, hoursByDay: nextHours };
  });
};

const sortCategoriesByEarliestStart = (categories: CategoryDef[]) => {
  const getEarliest = (c: CategoryDef) => {
    const fixed = c.fixedBlocksByDay;
    if (!fixed) return null;
    let min = Number.POSITIVE_INFINITY;
    (Object.keys(fixed) as DayKey[]).forEach((k) => {
      const blocks = fixed[k] || [];
      blocks.forEach((b) => {
        const s = parseTimeToMinutes(b.start);
        const e = parseTimeToMinutes(b.end);
        if (s == null || e == null) return;
        if (e <= s) return;
        min = Math.min(min, s);
      });
    });
    return Number.isFinite(min) ? min : null;
  };

  return categories
    .map((c, idx) => ({
      c,
      idx,
      t: getEarliest(c),
    }))
    .sort((a, b) => {
      const at = a.t ?? Number.POSITIVE_INFINITY;
      const bt = b.t ?? Number.POSITIVE_INFINITY;
      if (at !== bt) return at - bt;
      return a.idx - b.idx;
    })
    .map((x) => x.c);
};

const buildDefaultWifeCategories = (_hoursPerDay: number): CategoryDef[] => {
  const defaults: Array<{ id: string; label: string; baseRate: number }> = [
    { id: "therapist", label: "Therapist", baseRate: 150 },
    // Replaces prior "Counselor" role for Wife defaults.
    { id: "human-resources", label: "Human Resources", baseRate: 50 },
    { id: "executive-assistant", label: "Executive Assistant", baseRate: 35 },
    { id: "nurse", label: "Nurse", baseRate: 45 },
    { id: "chef", label: "Chef", baseRate: 55 },
    { id: "shopper", label: "Shopper", baseRate: 35 },
    { id: "chauffeur", label: "Chauffeur", baseRate: 25 },
    { id: "house-cleaner", label: "House Cleaner", baseRate: 35 },
  ];

  // Wife defaults are intentionally light-touch now: 10 minutes per day.
  const per = 10 / 60;
  const base = defaults.map((d) => {
    const baseRow: CategoryDef = {
      id: d.id,
      label: d.label,
      baseRate: d.baseRate,
      hoursByDay: {
        mon: per,
        tue: per,
        wed: per,
        thu: per,
        fri: per,
        sat: 0,
        sun: 0,
      },
    };

    if (d.id === "therapist") {
      const blocks: TimeBlock[] = [{ start: "19:45", end: "20:15" }];
      const h = computeHoursFromBlocks(blocks);
      return {
        ...baseRow,
        subtitle: "Marital & Family",
        fixedBlocksByDay: {
          tue: blocks,
          fri: blocks,
          sat: blocks,
        },
        hoursByDay: {
          mon: 0,
          tue: h,
          wed: 0,
          thu: 0,
          fri: h,
          sat: h,
          sun: 0,
        },
      };
    }

    if (d.id === "human-resources") {
      const monBlocks: TimeBlock[] = [
        { start: "09:10", end: "09:30" },
        { start: "13:40", end: "14:00" },
        { start: "18:05", end: "18:25" },
      ];
      const eveningBlock: TimeBlock = { start: "19:00", end: "19:30" };
      const sunBlocks: TimeBlock[] = [
        { start: "08:00", end: "08:30" },
        { start: "12:30", end: "13:00" },
      ];
      const blocks: TimeBlock[] = [{ start: "14:00", end: "16:00" }];
      const blocksWithEvening: TimeBlock[] = [...blocks, eveningBlock];
      const monWithEvening: TimeBlock[] = [...monBlocks, eveningBlock];
      const sunWithEvening: TimeBlock[] = [...sunBlocks, eveningBlock];
      return {
        ...baseRow,
        fixedBlocksByDay: {
          mon: monWithEvening,
          tue: blocksWithEvening,
          wed: blocksWithEvening,
          thu: blocksWithEvening,
          fri: blocksWithEvening,
          sat: blocksWithEvening,
          // Keep the special Sunday blocks.
          sun: sunWithEvening,
        },
        hoursByDay: {
          mon: computeHoursFromBlocks(monWithEvening),
          tue: computeHoursFromBlocks(blocksWithEvening),
          wed: computeHoursFromBlocks(blocksWithEvening),
          thu: computeHoursFromBlocks(blocksWithEvening),
          fri: computeHoursFromBlocks(blocksWithEvening),
          sat: computeHoursFromBlocks(blocksWithEvening),
          sun: computeHoursFromBlocks(sunWithEvening),
        },
      };
    }

    if (d.id === "nurse") {
      const blocks: TimeBlock[] = [{ start: "13:00", end: "13:10" }];
      const h = computeHoursFromBlocks(blocks);
      return {
        ...baseRow,
        fixedBlocksByDay: {
          tue: blocks,
          wed: blocks,
          thu: blocks,
          fri: blocks,
          sat: blocks,
        },
        hoursByDay: {
          mon: 0,
          tue: h,
          wed: h,
          thu: h,
          fri: h,
          sat: h,
          sun: 0,
        },
      };
    }

    if (d.id === "chef") {
      const blocks: TimeBlock[] = [
        { start: "11:00", end: "13:00" },
        { start: "17:00", end: "18:00" },
      ];
      const h = computeHoursFromBlocks(blocks);
      const sunBlocks: TimeBlock[] = [{ start: "15:00", end: "17:00" }];
      const sunHours = computeHoursFromBlocks(sunBlocks);
      return {
        ...baseRow,
        fixedBlocksByDay: {
          tue: blocks,
          wed: blocks,
          thu: blocks,
          fri: blocks,
          sat: blocks,
          sun: sunBlocks,
        },
        hoursByDay: {
          mon: 0,
          tue: h,
          wed: h,
          thu: h,
          fri: h,
          sat: h,
          sun: sunHours,
        },
      };
    }

    if (d.id === "chauffeur") {
      const wedBlocks: TimeBlock[] = [
        { start: "14:30", end: "15:00" },
        { start: "17:30", end: "18:45" },
      ];
      const wedHours = computeHoursFromBlocks(wedBlocks);
      return {
        ...baseRow,
        fixedBlocksByDay: {
          wed: wedBlocks,
        },
        hoursByDay: {
          mon: 0,
          tue: per,
          wed: wedHours,
          thu: per,
          fri: per,
          sat: per,
          sun: 0,
        },
      };
    }

    if (d.id === "house-cleaner") {
      const weekdayBlocks: TimeBlock[] = [
        { start: "11:00", end: "11:20" },
        { start: "16:00", end: "16:30" },
        { start: "18:30", end: "19:00" },
      ];
      const weekdayHours = computeHoursFromBlocks(weekdayBlocks);
      const sunBlocks: TimeBlock[] = [{ start: "18:30", end: "19:00" }];
      const sunHours = computeHoursFromBlocks(sunBlocks);
      return {
        ...baseRow,
        fixedBlocksByDay: {
          tue: weekdayBlocks,
          wed: weekdayBlocks,
          thu: weekdayBlocks,
          fri: weekdayBlocks,
          sat: weekdayBlocks,
          sun: sunBlocks,
        },
        hoursByDay: {
          mon: 0,
          tue: weekdayHours,
          wed: weekdayHours,
          thu: weekdayHours,
          fri: weekdayHours,
          sat: weekdayHours,
          sun: sunHours,
        },
      };
    }

    if (d.id === "executive-assistant") {
      const blocks: TimeBlock[] = [{ start: "13:00", end: "13:30" }];
      const h = computeHoursFromBlocks(blocks);
      return {
        ...baseRow,
        subtitle:
          "Calling vendors, scheduling appointments (health, mechanical, auto).",
        fixedBlocksByDay: {
          tue: blocks,
          sat: blocks,
        },
        hoursByDay: {
          mon: 0,
          tue: h,
          wed: 0,
          thu: 0,
          fri: 0,
          sat: h,
          sun: 0,
        },
      };
    }

    if (d.id === "shopper") {
      const wedBlocks: TimeBlock[] = [{ start: "15:00", end: "17:30" }];
      const satBlocks: TimeBlock[] = [{ start: "10:00", end: "13:00" }];
      const wedHours = computeHoursFromBlocks(wedBlocks);
      const satHours = computeHoursFromBlocks(satBlocks);
      return {
        ...baseRow,
        fixedBlocksByDay: {
          wed: wedBlocks,
          sat: satBlocks,
        },
        hoursByDay: {
          mon: 0,
          tue: 0,
          wed: wedHours,
          thu: 0,
          fri: 0,
          sat: satHours,
          sun: 0,
        },
      };
    }

    return baseRow;
  });

  const tutor: CategoryDef = {
    id: "homeschool-teacher",
    label: "Tutor",
    subtitle: "Core, Homec, Homesteading",
    baseRate: 100,
    hoursByDay: {
      mon: 0,
      tue: 4,
      wed: 4,
      thu: 4,
      fri: 4,
      sat: 4,
      sun: 0,
    },
    fixedBlocksByDay: {
      tue: [{ start: "09:00", end: "13:00" }],
      wed: [{ start: "09:00", end: "13:00" }],
      thu: [{ start: "09:00", end: "13:00" }],
      fri: [{ start: "09:00", end: "13:00" }],
      sat: [{ start: "09:00", end: "13:00" }],
    },
  };

  return sortCategoriesByEarliestStart(shiftMondayToSaturday([...base, tutor]));
};

const buildDefaultHusbandCategories = (hoursPerDay: number): CategoryDef[] => {
  // Defaults are light-touch since many of these are "as-needed" monthly roles.
  // Values are editable; this seeds something reasonable and keeps weekdays > weekends.
  const perDay = (weeklyHours: number) => ({
    mon: weeklyHours / 5,
    tue: weeklyHours / 5,
    wed: weeklyHours / 5,
    thu: weeklyHours / 5,
    fri: weeklyHours / 5,
    sat: 0,
    sun: 0,
  });

  // Convert monthly -> weekly using 4.333 weeks/month.
  const monthlyToWeekly = (monthlyHours: number) => monthlyHours / 4.333;

  const defaults: CategoryDef[] = [
    {
      id: "provider",
      label: "Provider",
      baseRate: 60,
      subtitle: "On-Site Security, Income Generation Specialist",
      hoursByDay: {
        mon: 8.5,
        tue: 8.5,
        wed: 8.5,
        thu: 8.5,
        fri: 8.5,
        sat: 0,
        sun: 0,
      },
      fixedBlocksByDay: {
        mon: [{ start: "09:00", end: "17:30" }],
        tue: [{ start: "09:00", end: "17:30" }],
        wed: [{ start: "09:00", end: "17:30" }],
        thu: [{ start: "09:00", end: "17:30" }],
        fri: [{ start: "09:00", end: "17:30" }],
      },
    },
    {
      id: "human-resources",
      label: "Human Resources",
      baseRate: 55,
      hoursByDay: perDay(2.5),
      // Keep this in the evening (PM) by default.
      fixedBlocksByDay: {
        mon: [{ start: "18:00", end: "18:30" }],
        tue: [{ start: "18:00", end: "18:30" }],
        wed: [{ start: "18:00", end: "18:30" }],
        thu: [{ start: "18:00", end: "18:30" }],
        fri: [{ start: "18:00", end: "18:30" }],
      },
      subtitle: "Fleet Manager, Dispute Resolution, Payroll",
    },
    {
      id: "house-cleaner",
      label: "House Cleaner",
      baseRate: 35,
      hoursByDay: {
        mon: 2.5,
        tue: 0,
        wed: 0,
        thu: 0,
        fri: 0,
        sat: 0,
        sun: 0,
      },
      fixedBlocksByDay: {
        mon: [
          { start: "07:00", end: "08:00" },
          { start: "17:00", end: "18:30" },
        ],
      },
    },
    {
      id: "travel-agent",
      label: "Travel Agent",
      baseRate: 45,
      hoursByDay: {
        mon: 0,
        tue: 10 / 60,
        wed: 0,
        thu: 0,
        fri: 0,
        sat: 0,
        sun: 0,
      },
      fixedBlocksByDay: {
        tue: [{ start: "09:00", end: "09:10" }],
      },
    },
    {
      id: "shopper",
      label: "Shopper",
      baseRate: 35,
      hoursByDay: {
        mon: 0,
        tue: 0.5,
        wed: 0,
        thu: 0.5,
        fri: 0,
        sat: 0,
        sun: 0,
      },
      fixedBlocksByDay: {
        tue: [{ start: "20:00", end: "20:30" }],
        thu: [{ start: "20:00", end: "20:30" }],
      },
    },
    {
      id: "bookkeeping-accounting",
      label: "Bookkeeping & Accounting",
      baseRate: 55,
      hoursByDay: {
        mon: 0,
        tue: 0.5,
        wed: 0.5,
        thu: 0.5,
        fri: 0.5,
        sat: 0.5,
        sun: 0,
      },
      fixedBlocksByDay: {
        tue: [{ start: "09:00", end: "09:30" }],
        wed: [{ start: "09:00", end: "09:30" }],
        thu: [{ start: "09:00", end: "09:30" }],
        fri: [{ start: "09:00", end: "09:30" }],
        sat: [{ start: "09:00", end: "09:30" }],
      },
    },
    {
      id: "executive-assistant",
      label: "Executive Assistant",
      subtitle:
        "Calling vendors, scheduling appointments (health, mechanical, auto).",
      baseRate: 35,
      hoursByDay: {
        mon: 0,
        tue: 0.5,
        wed: 0.5,
        thu: 0.5,
        fri: 0.5,
        sat: 0.5,
        sun: 0.5,
      },
      fixedBlocksByDay: {
        tue: [{ start: "13:00", end: "13:30" }],
        wed: [{ start: "13:00", end: "13:30" }],
        thu: [{ start: "13:00", end: "13:30" }],
        fri: [{ start: "13:00", end: "13:30" }],
        sat: [{ start: "13:00", end: "13:30" }],
        sun: [{ start: "13:00", end: "13:30" }],
      },
    },
    {
      id: "general-contractor",
      label: "General Contractor",
      baseRate: 150,
      hoursByDay: {
        mon: 0,
        tue: 0,
        wed: 0,
        thu: 0,
        fri: 0,
        sat: 7,
        sun: 0,
      },
      fixedBlocksByDay: {
        sat: [{ start: "09:00", end: "16:00" }],
      },
      subtitle: "Electrician, Plumber",
    },
    {
      id: "chauffeur",
      label: "Chauffeur",
      baseRate: 35,
      hoursByDay: {
        mon: 0,
        tue: 40 / 60,
        wed: 40 / 60,
        thu: 40 / 60,
        fri: 0,
        sat: 0,
        sun: 1,
      },
      fixedBlocksByDay: {
        tue: [{ start: "08:00", end: "08:40" }],
        wed: [{ start: "08:00", end: "08:40" }],
        thu: [{ start: "08:00", end: "08:40" }],
        sun: [
          { start: "08:00", end: "08:30" },
          { start: "12:30", end: "13:00" },
        ],
      },
    },
    {
      id: "chef",
      label: "Chef",
      baseRate: 55,
      hoursByDay: {
        mon: 0,
        tue: 10 / 60,
        wed: 10 / 60,
        thu: 10 / 60,
        fri: 10 / 60,
        sat: 10 / 60,
        sun: 0,
      },
      fixedBlocksByDay: {
        tue: [{ start: "07:00", end: "07:30" }],
        wed: [{ start: "07:00", end: "07:30" }],
        thu: [{ start: "07:00", end: "07:30" }],
        fri: [{ start: "07:00", end: "07:30" }],
        sat: [{ start: "07:00", end: "07:30" }],
      },
    },
    {
      id: "nurse",
      label: "Nurse",
      baseRate: 45,
      hoursByDay: {
        mon: 40 / 60,
        tue: 0,
        wed: 0,
        thu: 0,
        fri: 0,
        sat: 40 / 60,
        sun: 0,
      },
      fixedBlocksByDay: {
        mon: [
          { start: "10:00", end: "10:20" },
          { start: "16:00", end: "16:20" },
        ],
        sat: [
          { start: "13:00", end: "13:20" },
          { start: "16:00", end: "16:20" },
        ],
      },
    },
    {
      id: "therapist",
      label: "Therapist",
      baseRate: 150,
      subtitle: "Marital & Family",
      hoursByDay: {
        mon: 1,
        tue: 0,
        wed: 0,
        thu: 1,
        fri: 0,
        sat: 0,
        sun: 0,
      },
      fixedBlocksByDay: {
        mon: [{ start: "19:00", end: "20:00" }],
        thu: [{ start: "19:00", end: "20:00" }],
      },
    },
    {
      id: "tutor",
      label: "Tutor",
      subtitle: "PE, Mechanics, Contracting, Homesteading",
      baseRate: 100,
      hoursByDay: {
        mon: 0,
        tue: 0,
        wed: 0,
        thu: 0,
        fri: 0,
        sat: 2,
        sun: 2,
      },
      fixedBlocksByDay: {
        sat: [{ start: "10:00", end: "12:00" }],
        sun: [{ start: "13:00", end: "15:00" }],
      },
    },
  ];

  return sortCategoriesByEarliestStart(shiftMondayToSaturday(defaults));
};

const normalizeLoadedSettings = (
  raw: unknown,
  hoursPerDay: number
): SpouseValueSettingsV3 | null => {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as any;

  const childrenCount = clampNumber(Number(obj.childrenCount || 0), 0, 50);
  const homeschoolExtraPerChild = clampNumber(
    Number(obj.homeschoolExtraPerChild || 0),
    0,
    500
  );

  const hasNonEmptyArray = (value: unknown): value is unknown[] =>
    Array.isArray(value) && value.length > 0;

  const normalizeBlocksByDay = (value: unknown): BlocksByDay | undefined => {
    if (!value || typeof value !== "object") return undefined;
    const v = value as any;
    const out: BlocksByDay = {};
    (Object.keys(v) as DayKey[]).forEach((k) => {
      const arr = Array.isArray(v[k]) ? v[k] : [];
      const blocks = arr
        .map((b: any) => {
          const start = typeof b?.start === "string" ? b.start : null;
          const end = typeof b?.end === "string" ? b.end : null;
          if (!start || !end) return null;
          return { start, end };
        })
        .filter(Boolean) as TimeBlock[];
      if (blocks.length) out[k] = blocks;
    });
    return Object.keys(out).length ? out : undefined;
  };

  const normalizeCategories = (categoriesRaw: unknown): CategoryDef[] => {
    if (!Array.isArray(categoriesRaw)) return [];
    const categories: CategoryDef[] = categoriesRaw
      .map((c: any) => {
        if (!c || typeof c !== "object") return null;
        const id = typeof c.id === "string" ? c.id : null;
        const label = typeof c.label === "string" ? c.label : null;
        if (!id || !label) return null;
        const subtitle =
          typeof c.subtitle === "string" ? c.subtitle : undefined;
        const baseRate = Number(c.baseRate || 0);
        const hoursByDayRaw = c.hoursByDay || {};
        const hoursByDay: Record<DayKey, number> = {
          mon: Number(hoursByDayRaw.mon || 0),
          tue: Number(hoursByDayRaw.tue || 0),
          wed: Number(hoursByDayRaw.wed || 0),
          thu: Number(hoursByDayRaw.thu || 0),
          fri: Number(hoursByDayRaw.fri || 0),
          sat: Number(hoursByDayRaw.sat || 0),
          sun: Number(hoursByDayRaw.sun || 0),
        };

        const fixedRaw = c.fixedBlocksByDay;
        let fixedBlocksByDay: CategoryDef["fixedBlocksByDay"] | undefined;
        if (fixedRaw && typeof fixedRaw === "object") {
          const next: CategoryDef["fixedBlocksByDay"] = {};
          (Object.keys(fixedRaw) as DayKey[]).forEach((k) => {
            const arr = Array.isArray(fixedRaw[k]) ? fixedRaw[k] : [];
            const blocks = arr
              .map((b: any) => {
                const start = typeof b?.start === "string" ? b.start : null;
                const end = typeof b?.end === "string" ? b.end : null;
                if (!start || !end) return null;
                return { start, end };
              })
              .filter(Boolean) as TimeBlock[];
            if (blocks.length) next[k] = blocks;
          });
          if (Object.keys(next).length) fixedBlocksByDay = next;
        }

        return {
          id,
          label,
          subtitle,
          baseRate: Number.isFinite(baseRate) ? baseRate : 0,
          hoursByDay,
          fixedBlocksByDay,
        };
      })
      .filter(Boolean) as CategoryDef[];
    return categories;
  };

  const removeFriendCategory = (categories: CategoryDef[]) =>
    categories.filter((c) => c.id !== "friend");

  const removeFleetManagerCategory = (categories: CategoryDef[]) =>
    categories.filter((c) => c.id !== "fleet-manager");

  const removeElectricianPlumberCategories = (categories: CategoryDef[]) =>
    categories.filter((c) => c.id !== "electrician" && c.id !== "plumber");

  const ensureProviderBlocks = (categories: CategoryDef[]) => {
    return categories.map((c) => {
      if (c.id !== "provider") return c;
      if (c.fixedBlocksByDay && Object.keys(c.fixedBlocksByDay).length)
        return c;
      const blocks: TimeBlock[] = [{ start: "08:30", end: "18:30" }];
      const hours = computeHoursFromBlocks(blocks);
      return {
        ...c,
        fixedBlocksByDay: {
          tue: blocks,
          wed: blocks,
          thu: blocks,
          fri: blocks,
          sat: blocks,
        },
        hoursByDay: {
          ...c.hoursByDay,
          mon: 0,
          tue: hours,
          wed: hours,
          thu: hours,
          fri: hours,
          sat: hours,
        },
      };
    });
  };

  const ensureHumanResourcesBlocks = (categories: CategoryDef[]) => {
    return categories.map((c) => {
      if (c.id !== "human-resources") return c;
      if (c.fixedBlocksByDay && Object.keys(c.fixedBlocksByDay).length)
        return c;
      const blocks: TimeBlock[] = [{ start: "18:00", end: "18:30" }];
      const hours = computeHoursFromBlocks(blocks);
      return {
        ...c,
        fixedBlocksByDay: {
          tue: blocks,
          wed: blocks,
          thu: blocks,
          fri: blocks,
          sat: blocks,
        },
        hoursByDay: {
          ...c.hoursByDay,
          mon: 0,
          tue: hours,
          wed: hours,
          thu: hours,
          fri: hours,
          sat: hours,
        },
      };
    });
  };

  const ensureHouseCleanerMondayOnly = (categories: CategoryDef[]) => {
    return categories.map((c) => {
      if (c.id !== "house-cleaner") return c;
      const blocks: TimeBlock[] = [
        { start: "07:00", end: "08:00" },
        { start: "17:00", end: "18:30" },
      ];
      const hours = computeHoursFromBlocks(blocks);
      return {
        ...c,
        fixedBlocksByDay: {
          mon: blocks,
        },
        hoursByDay: {
          mon: hours,
          tue: 0,
          wed: 0,
          thu: 0,
          fri: 0,
          sat: 0,
          sun: 0,
        },
      };
    });
  };

  const normalizeMeterState = (value: unknown): MeterStateV1 | undefined => {
    if (!value || typeof value !== "object") return undefined;
    const v = value as any;
    if (v.version !== 1) return undefined;
    if (!v.items || typeof v.items !== "object") return undefined;
    if (!v.checked || typeof v.checked !== "object") return undefined;
    return v as MeterStateV1;
  };

  // v2/v3: full spouse settings
  if (obj.version === 3 || obj.version === 2) {
    if (
      !hasNonEmptyArray(obj.wifeCategories) ||
      !hasNonEmptyArray(obj.husbandCategories)
    ) {
      return null;
    }

    const wifeFromDb = normalizeCategories(obj.wifeCategories);
    const husbandFromDb = normalizeCategories(obj.husbandCategories);
    if (wifeFromDb.length === 0 || husbandFromDb.length === 0) return null;

    const wifeCategoriesRaw = removeFriendCategory(wifeFromDb);
    const husbandCategories = ensureHouseCleanerMondayOnly(
      ensureHumanResourcesBlocks(
        ensureProviderBlocks(
          removeElectricianPlumberCategories(
            removeFleetManagerCategory(removeFriendCategory(husbandFromDb))
          )
        )
      )
    );

    // If Wife categories accidentally got overwritten with Husband defaults, restore Wife defaults.
    const husbandOnlyIds = new Set([
      "provider",
      "human-resources",
      "travel-agent",
      "fleet-manager",
      "general-contractor",
      "electrician",
      "plumber",
    ]);
    const wifeLooksLikeHusband = wifeCategoriesRaw.some((c) =>
      husbandOnlyIds.has(c.id)
    );
    const wifeCategories = wifeLooksLikeHusband
      ? buildDefaultWifeCategories(hoursPerDay)
      : wifeCategoriesRaw;

    return {
      version: 3,
      childrenCount,
      homeschoolExtraPerChild,
      wifeCategories: shiftMondayToSaturday(wifeCategories),
      husbandCategories: shiftMondayToSaturday(husbandCategories),
      eliteBlocksByDay: normalizeBlocksByDay(obj.eliteBlocksByDay),
      showEliteEscortJoke: Boolean(obj.showEliteEscortJoke),
      showElitePremium: Boolean(obj.showElitePremium),
      wifeMeterState:
        obj.version === 3 ? normalizeMeterState(obj.wifeMeterState) : undefined,
      husbandMeterState:
        obj.version === 3
          ? normalizeMeterState(obj.husbandMeterState)
          : undefined,
    };
  }

  // v1 (legacy): categories live at top-level; treat as wife categories.
  if (!hasNonEmptyArray(obj.categories)) return null;
  const legacyBase = normalizeCategories(obj.categories);
  if (legacyBase.length === 0) return null;
  const legacyCategories = removeFriendCategory(legacyBase);

  return {
    version: 3,
    childrenCount,
    homeschoolExtraPerChild,
    wifeCategories: shiftMondayToSaturday(legacyCategories),
    husbandCategories: shiftMondayToSaturday(
      ensureHouseCleanerMondayOnly(
        ensureHumanResourcesBlocks(
          ensureProviderBlocks(
            removeFleetManagerCategory(
              removeElectricianPlumberCategories(
                removeFriendCategory(buildDefaultHusbandCategories(hoursPerDay))
              )
            )
          )
        )
      )
    ),
    eliteBlocksByDay: normalizeBlocksByDay(obj.eliteBlocksByDay),
    showEliteEscortJoke: Boolean(obj.showEliteEscortJoke),
    showElitePremium: Boolean(obj.showElitePremium),
    wifeMeterState: undefined,
    husbandMeterState: undefined,
  };
};

const SpouseValueCalculator: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { setShareHandler } = useShareAction();
  const [searchParams, setSearchParams] = useSearchParams();

  const hasHydratedRef = useRef(false);
  const saveTimerRef = useRef<number | null>(null);

  const [pageShareOpen, setPageShareOpen] = useState(false);

  // No global Start/End window anymore. This is only used for seeding defaults.
  const hoursPerDay = 14;

  const [childrenCount, setChildrenCount] = useState(6);
  const [homeschoolExtraPerChild, setHomeschoolExtraPerChild] = useState(5);

  const urlWho = searchParams.get("who");
  const urlView = searchParams.get("view");
  const initialActiveTab: "wife" | "husband" =
    urlWho === "husband" ? "husband" : "wife";
  const initialViewTab: "categories" | "calendar" =
    urlView === "calendar" ? "calendar" : "categories";

  const [activeTab, setActiveTab] = useState<"wife" | "husband">(
    initialActiveTab
  );
  const [viewTab, setViewTab] = useState<"categories" | "calendar">(
    initialViewTab
  );

  const [wifeMeterState, setWifeMeterState] = useState<MeterStateV1>(() =>
    loadMeterState("wife")
  );
  const [husbandMeterState, setHusbandMeterState] = useState<MeterStateV1>(() =>
    loadMeterState("husband")
  );
  const [meterDialogOpen, setMeterDialogOpen] = useState(false);
  const [meterDialogKind, setMeterDialogKind] =
    useState<MeterKind>("masculinity");
  // Meter modal always opens to the checklist view now.
  const [meterAddLabelBySection, setMeterAddLabelBySection] = useState<
    Record<MeterSection, string>
  >(() => ({
    Morning: "",
    Day: "",
    Evening: "",
    Night: "",
  }));
  const [meterAddPointsBySection, setMeterAddPointsBySection] = useState<
    Record<MeterSection, string>
  >(() => ({
    Morning: "1",
    Day: "1",
    Evening: "1",
    Night: "1",
  }));
  const [meterDrag, setMeterDrag] = useState<{
    from: MeterSection;
    itemId: string;
  } | null>(null);
  const [meterDragOver, setMeterDragOver] = useState<MeterSection | null>(null);

  const [eliteBlocksByDay, setEliteBlocksByDay] = useState<BlocksByDay>({});

  const [wifeCategories, setWifeCategories] = useState<CategoryDef[]>(() =>
    buildDefaultWifeCategories(hoursPerDay)
  );
  const [husbandCategories, setHusbandCategories] = useState<CategoryDef[]>(
    () => buildDefaultHusbandCategories(hoursPerDay)
  );

  const categories = activeTab === "wife" ? wifeCategories : husbandCategories;
  const setCategories =
    activeTab === "wife" ? setWifeCategories : setHusbandCategories;

  // Keep URL in sync with UI tabs (deep link + shareable state).
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("who", activeTab);
    next.set("view", viewTab);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, viewTab]);

  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryRate, setNewCategoryRate] = useState<number>(40);

  const [showEliteEscortJoke, setShowEliteEscortJoke] = useState(false);
  const [showElitePremium, setShowElitePremium] = useState(false);

  const meterState = activeTab === "wife" ? wifeMeterState : husbandMeterState;
  const setMeterState =
    activeTab === "wife" ? setWifeMeterState : setHusbandMeterState;

  useEffect(() => {
    saveMeterState("wife", wifeMeterState);
  }, [wifeMeterState]);

  useEffect(() => {
    saveMeterState("husband", husbandMeterState);
  }, [husbandMeterState]);

  const [dragFromIdx, setDragFromIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const [fixedEdit, setFixedEdit] = useState<{
    categoryId: CategoryId;
    dayKey: DayKey;
    blocks: TimeBlock[];
  } | null>(null);

  const openMeterDialog = (kind: MeterKind) => {
    setMeterDialogKind(kind);
    setMeterDialogOpen(true);
  };

  const toggleMeterItem = (itemId: string) => {
    setMeterState((prev) => ({
      ...prev,
      checked: { ...prev.checked, [itemId]: !prev.checked[itemId] },
    }));
  };

  const addMeterItem = (section: MeterSection) => {
    const label = (meterAddLabelBySection[section] || "").trim();
    if (!label) return;
    const points = clampNumber(
      Number(meterAddPointsBySection[section] || 0),
      -999,
      999
    );
    const nextItem: MeterItem = { id: createId(), label, points };
    setMeterState((prev) => ({
      ...prev,
      items: {
        ...prev.items,
        [meterDialogKind]: {
          ...prev.items[meterDialogKind],
          [section]: [...prev.items[meterDialogKind][section], nextItem],
        },
      },
    }));
    setMeterAddLabelBySection((prev) => ({ ...prev, [section]: "" }));
  };

  const deleteMeterItem = (section: MeterSection, itemId: string) => {
    setMeterState((prev) => {
      const nextItems = prev.items[meterDialogKind][section].filter(
        (i) => i.id !== itemId
      );
      const nextChecked = { ...prev.checked };
      delete nextChecked[itemId];
      return {
        ...prev,
        checked: nextChecked,
        items: {
          ...prev.items,
          [meterDialogKind]: {
            ...prev.items[meterDialogKind],
            [section]: nextItems,
          },
        },
      };
    });
  };

  const moveMeterItem = (
    from: MeterSection,
    to: MeterSection,
    itemId: string
  ) => {
    if (from === to) return;
    setMeterState((prev) => {
      const src = prev.items[meterDialogKind][from];
      const item = src.find((i) => i.id === itemId);
      if (!item) return prev;
      return {
        ...prev,
        items: {
          ...prev.items,
          [meterDialogKind]: {
            ...prev.items[meterDialogKind],
            [from]: src.filter((i) => i.id !== itemId),
            [to]: [...prev.items[meterDialogKind][to], item],
          },
        },
      };
    });
  };

  useEffect(() => {
    const load = async () => {
      try {
        const settings = await settingsService.getUserSettings();
        const loaded = normalizeLoadedSettings(
          settings.wife_value_settings,
          hoursPerDay
        );
        if (loaded) {
          setChildrenCount(loaded.childrenCount || 0);
          setHomeschoolExtraPerChild(loaded.homeschoolExtraPerChild || 0);
          setWifeCategories(loaded.wifeCategories);
          setHusbandCategories(loaded.husbandCategories);
          setEliteBlocksByDay(loaded.eliteBlocksByDay || {});
          setShowEliteEscortJoke(loaded.showEliteEscortJoke);
          setShowElitePremium(loaded.showElitePremium);
          if (loaded.wifeMeterState) setWifeMeterState(loaded.wifeMeterState);
          if (loaded.husbandMeterState)
            setHusbandMeterState(loaded.husbandMeterState);
        }
      } finally {
        hasHydratedRef.current = true;
      }
    };
    void load();
  }, []);

  useEffect(() => {
    if (!hasHydratedRef.current) return;
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      const cleanedWife = wifeCategories.map((c) => {
        const nextHours: Record<DayKey, number> = { ...c.hoursByDay };
        let nextFixed: CategoryDef["fixedBlocksByDay"] = c.fixedBlocksByDay
          ? { ...c.fixedBlocksByDay }
          : undefined;

        days.forEach((d) => {
          const blocks = nextFixed?.[d.key] || [];
          if (blocks.length) {
            const h = computeHoursFromBlocks(blocks);
            if (h <= 0.0001) {
              delete (nextFixed as any)[d.key];
              nextHours[d.key] = 0;
            } else {
              nextHours[d.key] = h;
            }
          } else if (Number(nextHours[d.key] || 0) <= 0.0001) {
            nextHours[d.key] = 0;
          }
        });

        if (nextFixed && Object.keys(nextFixed).length === 0)
          nextFixed = undefined;

        return { ...c, hoursByDay: nextHours, fixedBlocksByDay: nextFixed };
      });

      const cleanedHusband = husbandCategories.map((c) => {
        const nextHours: Record<DayKey, number> = { ...c.hoursByDay };
        let nextFixed: CategoryDef["fixedBlocksByDay"] = c.fixedBlocksByDay
          ? { ...c.fixedBlocksByDay }
          : undefined;

        days.forEach((d) => {
          const blocks = nextFixed?.[d.key] || [];
          if (blocks.length) {
            const h = computeHoursFromBlocks(blocks);
            if (h <= 0.0001) {
              delete (nextFixed as any)[d.key];
              nextHours[d.key] = 0;
            } else {
              nextHours[d.key] = h;
            }
          } else if (Number(nextHours[d.key] || 0) <= 0.0001) {
            nextHours[d.key] = 0;
          }
        });

        if (nextFixed && Object.keys(nextFixed).length === 0)
          nextFixed = undefined;

        return { ...c, hoursByDay: nextHours, fixedBlocksByDay: nextFixed };
      });

      const payload: SpouseValueSettingsV3 = {
        version: 3,
        childrenCount,
        homeschoolExtraPerChild,
        wifeCategories: cleanedWife,
        husbandCategories: cleanedHusband,
        eliteBlocksByDay,
        showEliteEscortJoke,
        showElitePremium,
        wifeMeterState,
        husbandMeterState,
      };
      void settingsService.saveUserSettings({
        wife_value_settings: payload as unknown,
      });
    }, 500);

    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
  }, [
    childrenCount,
    eliteBlocksByDay,
    husbandCategories,
    husbandMeterState,
    homeschoolExtraPerChild,
    showEliteEscortJoke,
    showElitePremium,
    wifeCategories,
    wifeMeterState,
  ]);

  useEffect(() => {
    setShareHandler(() => setPageShareOpen(true));
    return () => setShareHandler(null);
  }, [setShareHandler]);

  const sharePayload = useMemo(
    () => ({
      childrenCount,
      homeschoolExtraPerChild,
      wifeCategories,
      husbandCategories,
      eliteBlocksByDay,
      showEliteEscortJoke,
      showElitePremium,
      wifeMeterState,
      husbandMeterState,
    }),
    [
      childrenCount,
      husbandCategories,
      husbandMeterState,
      homeschoolExtraPerChild,
      showEliteEscortJoke,
      showElitePremium,
      wifeCategories,
      wifeMeterState,
      eliteBlocksByDay,
    ]
  );

  const getEffectiveRate = useCallback(
    (category: CategoryDef) => {
      // Tutor rate can be bumped per child (Wife legacy id + Husband tutor id).
      if (category.id === "homeschool-teacher" || category.id === "tutor") {
        return category.baseRate + childrenCount * homeschoolExtraPerChild;
      }
      // Human Resources accounts for children + spouse.
      if (category.id === "human-resources") {
        return (
          category.baseRate + (childrenCount + 1) * homeschoolExtraPerChild
        );
      }
      return category.baseRate;
    },
    [childrenCount, homeschoolExtraPerChild]
  );

  const getHoursForDay = useCallback(
    (category: CategoryDef, dayKey: DayKey) => {
      const blocks = category.fixedBlocksByDay?.[dayKey];
      if (blocks && blocks.length) return computeHoursFromBlocks(blocks);
      return Number(category.hoursByDay[dayKey] || 0);
    },
    []
  );

  const dayTotals = useMemo(() => {
    const totalsByDay = emptyHoursByDay();
    days.forEach((d) => {
      totalsByDay[d.key] = categories.reduce(
        (sum, c) => sum + getHoursForDay(c, d.key),
        0
      );
    });
    return totalsByDay;
  }, [categories, getHoursForDay]);

  const elite = useMemo(() => {
    const baseRate = 1150;
    const rate = baseRate * (showElitePremium ? 2 : 1);
    const weeklyHours = days.reduce(
      (sum, d) => sum + computeHoursFromBlocks(eliteBlocksByDay[d.key] || []),
      0
    );
    const weekly = rate * weeklyHours;
    const daily = weekly / 7;
    const annual = weekly * 52;
    return { rate, weeklyHours, daily, weekly, annual };
  }, [eliteBlocksByDay, showElitePremium]);

  const masculinityTotals = useMemo(
    () => calcMeterTotals(meterState, "masculinity"),
    [meterState]
  );
  const femininityTotals = useMemo(
    () => calcMeterTotals(meterState, "femininity"),
    [meterState]
  );

  const totals = useMemo(() => {
    const perCategory = categories.map((c) => {
      const weeklyHours = days.reduce(
        (sum, d) => sum + getHoursForDay(c, d.key),
        0
      );
      const rate = getEffectiveRate(c);
      const weekly = rate * weeklyHours;
      const annual = weekly * 52;
      return { id: c.id, weeklyHours, rate, weekly, annual };
    });

    const baseWeeklyTotal = perCategory.reduce(
      (sum, row) => sum + row.weekly,
      0
    );
    const baseAnnualTotal = perCategory.reduce(
      (sum, row) => sum + row.annual,
      0
    );

    // Include Elite Escort Services in the grand totals when enabled.
    const weeklyTotal =
      baseWeeklyTotal + (showEliteEscortJoke ? elite.weekly : 0);
    const annualTotal =
      baseAnnualTotal + (showEliteEscortJoke ? elite.annual : 0);

    const metersMultiplier =
      1 + masculinityTotals.bonusPct / 100 + femininityTotals.bonusPct / 100;
    const weeklyTotalWithMeters = weeklyTotal * metersMultiplier;
    const annualTotalWithMeters = annualTotal * metersMultiplier;

    const plannedWeeklyHours = days.reduce(
      (sum, d) => sum + Number(dayTotals[d.key] || 0),
      0
    );
    const plannedDailyAvgHours = plannedWeeklyHours / 7;
    const maxDayHours = Math.max(...days.map((d) => dayTotals[d.key] || 0));
    return {
      weeklyTotal: weeklyTotalWithMeters,
      annualTotal: annualTotalWithMeters,
      metersMultiplier,
      plannedWeeklyHours,
      plannedDailyAvgHours,
      maxDayHours,
    };
  }, [
    categories,
    dayTotals,
    elite.annual,
    elite.weekly,
    femininityTotals.bonusPct,
    getEffectiveRate,
    getHoursForDay,
    masculinityTotals.bonusPct,
    showEliteEscortJoke,
  ]);

  const scheduleMap = useMemo(() => {
    // Global Start/End window has been removed. We keep a deterministic baseline
    // for stacking non-fixed roles so the table can show meaningful time ranges.
    const baseStartMin = 6 * 60 + 30;
    const map: Record<
      DayKey,
      Record<
        CategoryId,
        {
          startMin: number;
          endMin: number;
          durationMin: number;
        }
      >
    > = {
      mon: {},
      tue: {},
      wed: {},
      thu: {},
      fri: {},
      sat: {},
      sun: {},
    };

    days.forEach((d) => {
      const reserved: Array<{ start: number; end: number }> = [];
      categories.forEach((c) => {
        const blocks = c.fixedBlocksByDay?.[d.key] || [];
        blocks.forEach((b) => {
          const s = parseTimeToMinutes(b.start);
          const e = parseTimeToMinutes(b.end);
          if (s == null || e == null) return;
          if (e <= s) return;
          reserved.push({ start: s, end: e });
        });
      });
      if (showEliteEscortJoke) {
        const eliteBlocks = eliteBlocksByDay[d.key] || [];
        eliteBlocks.forEach((b) => {
          const s = parseTimeToMinutes(b.start);
          const e = parseTimeToMinutes(b.end);
          if (s == null || e == null) return;
          if (e <= s) return;
          reserved.push({ start: s, end: e });
        });
      }
      reserved.sort((a, b) => a.start - b.start);
      const merged: Array<{ start: number; end: number }> = [];
      reserved.forEach((r) => {
        const last = merged[merged.length - 1];
        if (!last || r.start > last.end) merged.push({ ...r });
        else last.end = Math.max(last.end, r.end);
      });

      const advancePastReserved = (cursor: number) => {
        let cur = cursor;
        merged.forEach((r) => {
          if (cur >= r.start && cur < r.end) cur = r.end;
        });
        return cur;
      };

      const nextOverlap = (start: number, end: number) => {
        return merged.find((r) => start < r.start && end > r.start) || null;
      };

      let cursor = advancePastReserved(baseStartMin);
      categories.forEach((c) => {
        const fixed = c.fixedBlocksByDay?.[d.key];
        if (fixed && fixed.length) return;
        const hours = getHoursForDay(c, d.key);
        const durationMin = Math.max(0, hours * 60);
        if (durationMin <= 0) {
          map[d.key][c.id] = {
            startMin: cursor,
            endMin: cursor,
            durationMin: 0,
          };
          return;
        }
        cursor = advancePastReserved(cursor);
        let start = cursor;
        let end = start + durationMin;
        const overlap = nextOverlap(start, end);
        if (overlap) {
          start = advancePastReserved(overlap.end);
          end = start + durationMin;
        }
        map[d.key][c.id] = {
          startMin: start,
          endMin: end,
          durationMin,
        };
        cursor = end;
      });
    });

    // Calendar display range: derive from actual blocks, with padding.
    let minStart = Number.POSITIVE_INFINITY;
    let maxEnd = Number.NEGATIVE_INFINITY;

    days.forEach((d) => {
      categories.forEach((c) => {
        const fixed = c.fixedBlocksByDay?.[d.key] || [];
        fixed.forEach((b) => {
          const s = parseTimeToMinutes(b.start);
          const e = parseTimeToMinutes(b.end);
          if (s == null || e == null) return;
          if (e <= s) return;
          minStart = Math.min(minStart, s);
          maxEnd = Math.max(maxEnd, e);
        });

        const sched = map[d.key][c.id];
        if (sched && sched.durationMin > 0) {
          minStart = Math.min(minStart, sched.startMin);
          maxEnd = Math.max(maxEnd, sched.endMin);
        }
      });

      if (showEliteEscortJoke) {
        const eliteBlocks = eliteBlocksByDay[d.key] || [];
        eliteBlocks.forEach((b) => {
          const s = parseTimeToMinutes(b.start);
          const e = parseTimeToMinutes(b.end);
          if (s == null || e == null) return;
          if (e <= s) return;
          minStart = Math.min(minStart, s);
          maxEnd = Math.max(maxEnd, e);
        });
      }
    });

    const padding = 30;
    const startMin =
      Number.isFinite(minStart) && Number.isFinite(maxEnd)
        ? clampNumber(minStart - padding, 0, 24 * 60)
        : 0;
    const endMin =
      Number.isFinite(minStart) && Number.isFinite(maxEnd)
        ? clampNumber(maxEnd + padding, 0, 24 * 60)
        : 24 * 60;

    return { startMin, endMin: Math.max(endMin, startMin + 1), map };
  }, [categories, eliteBlocksByDay, getHoursForDay, showEliteEscortJoke]);

  const handleResetActiveDefaults = () => {
    if (activeTab === "wife") {
      setWifeCategories(buildDefaultWifeCategories(hoursPerDay));
    } else {
      setHusbandCategories(buildDefaultHusbandCategories(hoursPerDay));
    }
    setFixedEdit(null);
  };

  const moveCategory = (fromIdx: number, toIdx: number) => {
    setCategories((prev) => {
      if (fromIdx < 0 || fromIdx >= prev.length) return prev;
      if (toIdx < 0 || toIdx >= prev.length) return prev;
      if (fromIdx === toIdx) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  const handleAddCategory = () => {
    const name = newCategoryName.trim();
    if (!name) return;
    const slug = slugify(name);
    const id = slug ? `${slug}-${createId()}` : createId();
    const rate = clampNumber(Number(newCategoryRate || 0), 0, 5000);
    const per =
      hoursPerDay > 0 ? hoursPerDay / Math.max(1, categories.length + 1) : 0;
    setCategories((prev) => [
      ...prev,
      {
        id,
        label: name,
        baseRate: rate,
        hoursByDay: {
          mon: per,
          tue: per,
          wed: per,
          thu: per,
          fri: per,
          sat: 0,
          sun: 0,
        },
      },
    ]);
    setAddCategoryOpen(false);
    setNewCategoryName("");
    setNewCategoryRate(40);
  };

  // Shared/public pages can render with a light theme; keep the calculator's
  // main surfaces dark regardless so the UX matches the app.
  const calculatorPaperBg = isDarkMode
    ? theme.palette.background.paper
    : "#141414";

  const panelSx = {
    p: 2,
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    bgcolor: calculatorPaperBg,
  } as const;

  const tableStickyBg = calculatorPaperBg;
  // Must be fully opaque for sticky columns.
  const tableSummaryBg = lighten(calculatorPaperBg, isDarkMode ? 0.12 : 0.06);
  const roleColWidth = 150;
  const rateColWidth = 96;
  const dayColWidth = 150;
  const summaryColWidth = 116;
  const meterDialogTitle =
    meterDialogKind === "masculinity"
      ? "Masculinity Meter"
      : "Femininity Meter";
  const meterDialogTotals =
    meterDialogKind === "masculinity" ? masculinityTotals : femininityTotals;
  const meterCardSx = {
    px: 1.25,
    py: 1,
    borderRadius: 2,
    cursor: "pointer",
    minWidth: 220,
    bgcolor: alpha(theme.palette.action.hover, 0.35),
    border: "1px solid",
    borderColor: "divider",
    "&:hover": {
      bgcolor: alpha(theme.palette.action.hover, 0.55),
    },
  } as const;

  return (
    <Box>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900 }}>
            Spouse Value Calculator
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This tool is intended as a joke, and a means to honor one another.
            The true compensitory value of a spouse is paid in the currency of
            love. From love comes connection, from connection comes trust, trust
            &gt; vulnerability &gt; intimacy &gt; peak sexuality &gt; peak
            connection and partnership.
          </Typography>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          textColor="inherit"
          indicatorColor="primary"
          sx={{ px: 0 }}
        >
          <Tab value="wife" label="Wife" />
          <Tab value="husband" label="Husband" />
        </Tabs>

        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", lg: "stretch" }}
        >
          <Paper sx={{ ...panelSx, flex: 1 }}>
            <Stack spacing={1.5}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  flexWrap: "wrap",
                  rowGap: 1,
                  justifyContent: "flex-start",
                }}
              >
                <Stack direction="column" spacing={0.25} alignItems="flex-end">
                  <Typography variant="body2" sx={{ fontWeight: 900 }}>
                    {formatHours(totals.plannedWeeklyHours)} / week planned
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 900 }}>
                    {formatHours(totals.plannedDailyAvgHours)} / day avg
                  </Typography>
                </Stack>

                {activeTab === "wife" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 900 }}>
                      Volume of Children
                    </Typography>
                    <TextField
                      size="small"
                      value={childrenCount}
                      onChange={(e) =>
                        setChildrenCount(
                          clampNumber(Number(e.target.value || 0), 0, 50)
                        )
                      }
                      inputProps={{ inputMode: "numeric" }}
                      sx={{
                        width: 92,
                        "& input": { textAlign: "right" },
                      }}
                    />
                  </Box>
                )}

                <Tooltip
                  title={
                    activeTab === "wife"
                      ? "Reset Wife roles, rates, and schedule to defaults"
                      : "Reset Husband roles, rates, and schedule to defaults"
                  }
                >
                  <Button
                    size="small"
                    color="inherit"
                    startIcon={<RestartAltRoundedIcon />}
                    onClick={handleResetActiveDefaults}
                  >
                    Reset to Defaults
                  </Button>
                </Tooltip>
              </Stack>
            </Stack>
          </Paper>

          <Stack
            direction={{ xs: "column", md: "row", lg: "column" }}
            spacing={1}
            justifyContent="flex-end"
            sx={{ flexWrap: "wrap" }}
          >
            <Paper
              onClick={() => openMeterDialog("masculinity")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openMeterDialog("masculinity");
                }
              }}
              sx={meterCardSx}
            >
              <Stack spacing={0.5}>
                <Stack
                  direction="row"
                  alignItems="baseline"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Typography variant="caption" sx={{ fontWeight: 900 }}>
                    Masculinity Meter
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 800 }}
                  >
                    {masculinityTotals.earned}/{masculinityTotals.max} (
                    {masculinityTotals.tier}
                    {masculinityTotals.bonusPct > 0
                      ? ` +${masculinityTotals.bonusPct}%`
                      : ""}
                    )
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={masculinityTotals.pct * 100}
                  sx={{
                    height: 8,
                    borderRadius: 8,
                    bgcolor: alpha(theme.palette.common.white, 0.06),
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 8,
                    },
                  }}
                />
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{
                    fontSize: 11,
                    color: alpha(theme.palette.text.secondary, 0.9),
                    fontWeight: 900,
                  }}
                >
                  <Box>Low</Box>
                  <Box>Medium</Box>
                  <Box>High</Box>
                </Stack>
              </Stack>
            </Paper>

            <Paper
              onClick={() => openMeterDialog("femininity")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openMeterDialog("femininity");
                }
              }}
              sx={meterCardSx}
            >
              <Stack spacing={0.5}>
                <Stack
                  direction="row"
                  alignItems="baseline"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Typography variant="caption" sx={{ fontWeight: 900 }}>
                    Femininity Meter
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 800 }}
                  >
                    {femininityTotals.earned}/{femininityTotals.max} (
                    {femininityTotals.tier}
                    {femininityTotals.bonusPct > 0
                      ? ` +${femininityTotals.bonusPct}%`
                      : ""}
                    )
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={femininityTotals.pct * 100}
                  sx={{
                    height: 8,
                    borderRadius: 8,
                    bgcolor: alpha(theme.palette.common.white, 0.06),
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 8,
                    },
                  }}
                />
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{
                    fontSize: 11,
                    color: alpha(theme.palette.text.secondary, 0.9),
                    fontWeight: 900,
                  }}
                >
                  <Box>Low</Box>
                  <Box>Medium</Box>
                  <Box>High</Box>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Stack>

        <Tabs
          value={viewTab}
          onChange={(_, v) => setViewTab(v)}
          textColor="inherit"
          indicatorColor="primary"
          sx={{ px: 0 }}
        >
          <Tab value="categories" label="Roles, Rate & Schedule" />
          <Tab value="calendar" label="Calendar" />
        </Tabs>

        {viewTab === "categories" && (
          <Paper sx={panelSx}>
            <Stack spacing={1}>
              <Box sx={{ width: "100%", overflowX: "auto" }}>
                <Table
                  size="small"
                  sx={{
                    minWidth:
                      roleColWidth +
                      rateColWidth +
                      dayColWidth * days.length +
                      summaryColWidth * 3,
                    tableLayout: "fixed",
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 800,
                          position: "sticky",
                          left: 0,
                          zIndex: 3,
                          bgcolor: tableStickyBg,
                          width: roleColWidth,
                          minWidth: roleColWidth,
                          borderRight: "1px solid",
                          borderRightColor: "divider",
                          pl: 0,
                          pr: 0.25,
                        }}
                      >
                        Role
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 800,
                          position: "sticky",
                          left: roleColWidth,
                          zIndex: 3,
                          bgcolor: tableStickyBg,
                          width: rateColWidth,
                          minWidth: rateColWidth,
                          borderRight: "1px solid",
                          borderRightColor: "divider",
                          px: 0.5,
                        }}
                        align="right"
                      >
                        Rate
                      </TableCell>
                      {days.map((d) => (
                        <TableCell
                          key={`head-${d.key}`}
                          sx={{
                            fontWeight: 800,
                            width: dayColWidth,
                            minWidth: dayColWidth,
                            px: 0.5,
                          }}
                          align="right"
                        >
                          {d.label}
                        </TableCell>
                      ))}
                      <TableCell
                        sx={{
                          fontWeight: 800,
                          position: "sticky",
                          right: summaryColWidth * 2,
                          zIndex: 3,
                          bgcolor: tableSummaryBg,
                          width: summaryColWidth,
                          minWidth: summaryColWidth,
                          borderLeft: "1px solid",
                          borderLeftColor: "divider",
                          whiteSpace: "nowrap",
                        }}
                        align="right"
                      >
                        Daily
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 800,
                          position: "sticky",
                          right: summaryColWidth,
                          zIndex: 3,
                          bgcolor: tableSummaryBg,
                          width: summaryColWidth,
                          minWidth: summaryColWidth,
                          whiteSpace: "nowrap",
                        }}
                        align="right"
                      >
                        Weekly
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 800,
                          position: "sticky",
                          right: 0,
                          zIndex: 3,
                          bgcolor: tableSummaryBg,
                          width: summaryColWidth,
                          minWidth: summaryColWidth,
                          whiteSpace: "nowrap",
                        }}
                        align="right"
                      >
                        Annually
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categories.map((c, idx) => {
                      const weeklyHours = days.reduce(
                        (sum, d) => sum + Number(c.hoursByDay[d.key] || 0),
                        0
                      );
                      const effectiveRate = getEffectiveRate(c);
                      const weekly = effectiveRate * weeklyHours;
                      const dailyAvg = weekly / 7;
                      const annual = weekly * 52;
                      const isTutor =
                        c.id === "homeschool-teacher" || c.id === "tutor";
                      const isHR = c.id === "human-resources";
                      const tutorBump = childrenCount * homeschoolExtraPerChild;
                      const hrUnits = childrenCount + 1;
                      const hrBump = hrUnits * homeschoolExtraPerChild;

                      return (
                        <TableRow
                          key={c.id}
                          hover
                          onDragOver={(e) => {
                            if (dragFromIdx == null) return;
                            e.preventDefault();
                            if (dragOverIdx !== idx) setDragOverIdx(idx);
                          }}
                          onDragLeave={() => {
                            if (dragOverIdx === idx) setDragOverIdx(null);
                          }}
                          onDrop={(e) => {
                            if (dragFromIdx == null) return;
                            e.preventDefault();
                            const from = dragFromIdx;
                            let to = idx;
                            // Drop inserts *before* the target row.
                            if (from < to) to -= 1;
                            moveCategory(from, to);
                            setDragFromIdx(null);
                            setDragOverIdx(null);
                          }}
                          onDragEnd={() => {
                            setDragFromIdx(null);
                            setDragOverIdx(null);
                          }}
                          sx={{
                            ...(dragOverIdx === idx
                              ? {
                                  outline: "2px solid",
                                  outlineColor: "primary.main",
                                  outlineOffset: "-2px",
                                }
                              : null),
                          }}
                        >
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              position: "sticky",
                              left: 0,
                              zIndex: 2,
                              bgcolor: tableStickyBg,
                              width: roleColWidth,
                              minWidth: roleColWidth,
                              borderRight: "1px solid",
                              borderRightColor: "divider",
                              pl: 0,
                              pr: 0.25,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <Box
                                draggable
                                onDragStart={(e) => {
                                  setDragFromIdx(idx);
                                  setDragOverIdx(idx);
                                  try {
                                    e.dataTransfer.effectAllowed = "move";
                                    e.dataTransfer.setData("text/plain", c.id);
                                  } catch {
                                    // ignore
                                  }
                                }}
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  cursor: "grab",
                                  color: "text.disabled",
                                  "&:active": { cursor: "grabbing" },
                                }}
                                aria-label="Drag to reorder"
                              >
                                <DragIndicatorRoundedIcon fontSize="small" />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  sx={{ fontWeight: 800, fontSize: 13 }}
                                >
                                  {c.label}
                                </Typography>
                                {c.subtitle && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {c.subtitle}
                                  </Typography>
                                )}
                                {isTutor && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    + {formatCurrency(homeschoolExtraPerChild)}
                                    /hr × {childrenCount} ={" "}
                                    {formatCurrency(tutorBump)}/hr
                                  </Typography>
                                )}
                                {isHR && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    + {formatCurrency(homeschoolExtraPerChild)}
                                    /hr × {hrUnits} = {formatCurrency(hrBump)}
                                    /hr
                                  </Typography>
                                )}
                              </Box>
                              <Box
                                sx={{ ml: "auto", display: "flex", gap: 0.25 }}
                              >
                                {/* Reordering is persisted via settings; hide move controls. */}
                              </Box>
                            </Stack>
                          </TableCell>

                          <TableCell
                            align="right"
                            sx={{
                              position: "sticky",
                              left: roleColWidth,
                              zIndex: 2,
                              bgcolor: tableStickyBg,
                              width: rateColWidth,
                              minWidth: rateColWidth,
                              borderRight: "1px solid",
                              borderRightColor: "divider",
                              px: 0.5,
                            }}
                          >
                            {c.id === "homeschool-teacher" ||
                            c.id === "tutor" ? (
                              <Stack
                                spacing={0.75}
                                sx={{ alignItems: "flex-end", width: "100%" }}
                              >
                                <TextField
                                  size="small"
                                  label="Base rate"
                                  value={c.baseRate}
                                  onChange={(e) => {
                                    const next = clampNumber(
                                      Number(e.target.value || 0),
                                      0,
                                      5000
                                    );
                                    setCategories((prev) =>
                                      prev.map((row) =>
                                        row.id === c.id
                                          ? { ...row, baseRate: next }
                                          : row
                                      )
                                    );
                                  }}
                                  inputProps={{ inputMode: "decimal" }}
                                  InputLabelProps={{ shrink: true }}
                                  fullWidth
                                  sx={{
                                    minWidth: 0,
                                    "& input": { textAlign: "right" },
                                  }}
                                />
                                <TextField
                                  size="small"
                                  label="+/hr per child"
                                  value={homeschoolExtraPerChild}
                                  onChange={(e) =>
                                    setHomeschoolExtraPerChild(
                                      clampNumber(
                                        Number(e.target.value || 0),
                                        0,
                                        500
                                      )
                                    )
                                  }
                                  inputProps={{ inputMode: "decimal" }}
                                  InputLabelProps={{ shrink: true }}
                                  fullWidth
                                  sx={{
                                    minWidth: 0,
                                    "& input": { textAlign: "right" },
                                  }}
                                />
                              </Stack>
                            ) : (
                              <TextField
                                size="small"
                                value={c.baseRate}
                                onChange={(e) => {
                                  const next = clampNumber(
                                    Number(e.target.value || 0),
                                    0,
                                    5000
                                  );
                                  setCategories((prev) =>
                                    prev.map((row) =>
                                      row.id === c.id
                                        ? { ...row, baseRate: next }
                                        : row
                                    )
                                  );
                                }}
                                inputProps={{ inputMode: "decimal" }}
                                fullWidth
                                sx={{
                                  minWidth: 0,
                                  "& input": {
                                    textAlign: "right",
                                  },
                                }}
                              />
                            )}
                          </TableCell>

                          {days.map((d) => (
                            <TableCell
                              key={`${c.id}-${d.key}`}
                              align="right"
                              sx={{
                                width: dayColWidth,
                                minWidth: dayColWidth,
                                px: 0.5,
                              }}
                            >
                              {(() => {
                                const fixedBlocks =
                                  c.fixedBlocksByDay?.[d.key] || null;
                                const hours = getHoursForDay(c, d.key);
                                const sched = scheduleMap.map[d.key][c.id];

                                const ranges: Array<{
                                  startMin: number;
                                  endMin: number;
                                }> =
                                  fixedBlocks && fixedBlocks.length
                                    ? (fixedBlocks
                                        .map((b) => {
                                          const s = parseTimeToMinutes(b.start);
                                          const e = parseTimeToMinutes(b.end);
                                          if (s == null || e == null)
                                            return null;
                                          if (e <= s) return null;
                                          return { startMin: s, endMin: e };
                                        })
                                        .filter(Boolean) as Array<{
                                        startMin: number;
                                        endMin: number;
                                      }>)
                                    : hours > 0 &&
                                        sched &&
                                        sched.durationMin > 0
                                      ? [
                                          {
                                            startMin: sched.startMin,
                                            endMin: sched.endMin,
                                          },
                                        ]
                                      : [];

                                const right =
                                  hours > 0
                                    ? `${formatHoursNumber(hours)}h`
                                    : "—";
                                const canClear =
                                  ranges.length > 0 ||
                                  Number(c.hoursByDay[d.key] || 0) > 0.0001;

                                const canEdit =
                                  (fixedBlocks && fixedBlocks.length) ||
                                  (sched && sched.durationMin > 0);

                                return (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "flex-end",
                                    }}
                                  >
                                    <Box
                                      onClick={() => {
                                        if (!canEdit) return;

                                        if (fixedBlocks && fixedBlocks.length) {
                                          setFixedEdit({
                                            categoryId: c.id,
                                            dayKey: d.key,
                                            blocks: fixedBlocks,
                                          });
                                          return;
                                        }

                                        // For stacked (non-fixed) rows, convert the computed range
                                        // into a fixed block via the same modal.
                                        if (!sched || sched.durationMin <= 0)
                                          return;
                                        setFixedEdit({
                                          categoryId: c.id,
                                          dayKey: d.key,
                                          blocks: [
                                            {
                                              start: minutesToHHMM(
                                                sched.startMin
                                              ),
                                              end: minutesToHHMM(sched.endMin),
                                            },
                                          ],
                                        });
                                      }}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "flex-start",
                                        gap: 1,
                                        cursor: canEdit ? "pointer" : "default",
                                        userSelect: "none",
                                      }}
                                    >
                                      <Stack
                                        spacing={0.25}
                                        sx={{
                                          minWidth: 0,
                                          alignItems: "flex-end",
                                        }}
                                      >
                                        {ranges.length === 0 ? (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 800,
                                              color: "text.secondary",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            —
                                          </Typography>
                                        ) : (
                                          ranges.map((r, i) => (
                                            <Typography
                                              key={`${c.id}-${d.key}-r-${i}`}
                                              variant="caption"
                                              sx={{
                                                fontWeight: 800,
                                                color: "text.secondary",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                maxWidth: 240,
                                                textAlign: "right",
                                              }}
                                            >
                                              <TimeLabel minutes={r.startMin} />{" "}
                                              - <TimeLabel minutes={r.endMin} />
                                            </Typography>
                                          ))
                                        )}

                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontWeight: 900,
                                            whiteSpace: "nowrap",
                                            textAlign: "right",
                                          }}
                                        >
                                          {right}
                                        </Typography>
                                      </Stack>

                                      <Tooltip title="Clear time">
                                        <span>
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setCategories((prev) =>
                                                prev.map((row) => {
                                                  if (row.id !== c.id)
                                                    return row;
                                                  const nextHoursByDay = {
                                                    ...row.hoursByDay,
                                                    [d.key]: 0,
                                                  };
                                                  const fixed =
                                                    row.fixedBlocksByDay;
                                                  if (!fixed || !fixed[d.key]) {
                                                    return {
                                                      ...row,
                                                      hoursByDay:
                                                        nextHoursByDay,
                                                    };
                                                  }
                                                  const {
                                                    [d.key]: _removed,
                                                    ...rest
                                                  } = fixed as Record<
                                                    string,
                                                    TimeBlock[]
                                                  >;
                                                  const nextFixed =
                                                    Object.keys(rest).length > 0
                                                      ? (rest as any)
                                                      : undefined;
                                                  return {
                                                    ...row,
                                                    fixedBlocksByDay: nextFixed,
                                                    hoursByDay: nextHoursByDay,
                                                  };
                                                })
                                              );
                                              setFixedEdit((cur) => {
                                                if (
                                                  cur?.categoryId === c.id &&
                                                  cur?.dayKey === d.key
                                                )
                                                  return null;
                                                return cur;
                                              });
                                            }}
                                            disabled={!canClear}
                                          >
                                            <ClearRoundedIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </Box>
                                  </Box>
                                );
                              })()}
                            </TableCell>
                          ))}

                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 700,
                              position: "sticky",
                              right: summaryColWidth * 2,
                              zIndex: 2,
                              bgcolor: tableSummaryBg,
                              width: summaryColWidth,
                              minWidth: summaryColWidth,
                              borderLeft: "1px solid",
                              borderLeftColor: "divider",
                            }}
                          >
                            {formatCurrency(dailyAvg)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 700,
                              position: "sticky",
                              right: summaryColWidth,
                              zIndex: 2,
                              bgcolor: tableSummaryBg,
                              width: summaryColWidth,
                              minWidth: summaryColWidth,
                            }}
                          >
                            {formatCurrency(weekly)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 700,
                              position: "sticky",
                              right: 0,
                              zIndex: 2,
                              bgcolor: tableSummaryBg,
                              width: summaryColWidth,
                              minWidth: summaryColWidth,
                            }}
                          >
                            {formatCurrency(annual)}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    <TableRow
                      hover
                      sx={{
                        "& td": {
                          borderTop: "1px dashed",
                          borderTopColor: "divider",
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 700 }}>
                        <Tooltip
                          arrow
                          placement="top"
                          title="This is a joke. Great sexuality in marriage is a gift to both spouses. The more feminine and masculine they are the more rejuvenating it is. But it definitely is expensive to fund, and money plays a part in that when you consider the cost of self-care, gym, clothing, makeup, therapy for a sound mind, investment in spiritual retreats, etc..."
                        >
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "flex-start",
                              ml: -1,
                            }}
                          >
                            <Checkbox
                              checked={showEliteEscortJoke}
                              onChange={(e) => {
                                const next = e.target.checked;
                                setShowEliteEscortJoke(next);
                                if (next) {
                                  setEliteBlocksByDay((prev) => {
                                    const hasAny = days.some(
                                      (d) => (prev[d.key] || []).length > 0
                                    );
                                    return hasAny
                                      ? prev
                                      : buildDefaultEliteBlocksByDay();
                                  });
                                }
                              }}
                              size="small"
                              sx={{ ml: 0, mr: 0.25, mt: "-2px" }}
                            />
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ cursor: "help" }}
                              >
                                <Box
                                  component="span"
                                  sx={{ textDecoration: "line-through" }}
                                >
                                  Sex
                                </Box>{" "}
                                Elite Escort Services
                                <Tooltip
                                  title="Non-Refundable"
                                  arrow
                                  placement="top"
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      ml: 0.25,
                                      fontWeight: 900,
                                      cursor: "help",
                                    }}
                                  >
                                    *
                                  </Box>
                                </Tooltip>
                              </Typography>
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Checkbox
                                  size="small"
                                  disabled={!showEliteEscortJoke}
                                  checked={showElitePremium}
                                  onChange={(e) =>
                                    setShowElitePremium(e.target.checked)
                                  }
                                  sx={{ p: 0, ml: 0, mr: 0.5, mt: "-2px" }}
                                />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Add lifetime guarantee. (+100%)
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 700,
                          position: "sticky",
                          left: roleColWidth,
                          zIndex: 2,
                          bgcolor: tableStickyBg,
                          width: rateColWidth,
                          minWidth: rateColWidth,
                          borderRight: "1px solid",
                          borderRightColor: "divider",
                          px: 0.5,
                        }}
                      >
                        {showEliteEscortJoke ? formatCurrency(elite.rate) : "—"}
                      </TableCell>
                      {days.map((d) => (
                        <TableCell
                          key={`escort-${d.key}`}
                          align="right"
                          sx={{
                            fontWeight: 700,
                            width: dayColWidth,
                            minWidth: dayColWidth,
                            px: 0.5,
                          }}
                        >
                          {(() => {
                            if (!showEliteEscortJoke) return "—";
                            const blocks = eliteBlocksByDay[d.key] || [];
                            const ranges = blocks
                              .map((b) => {
                                const s = parseTimeToMinutes(b.start);
                                const e = parseTimeToMinutes(b.end);
                                if (s == null || e == null) return null;
                                if (e <= s) return null;
                                return { startMin: s, endMin: e };
                              })
                              .filter(Boolean) as Array<{
                              startMin: number;
                              endMin: number;
                            }>;
                            const hours = computeHoursFromBlocks(blocks);
                            return (
                              <Stack
                                spacing={0.25}
                                sx={{ minWidth: 0, alignItems: "flex-end" }}
                              >
                                {ranges.length === 0 ? (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 800,
                                      color: "text.secondary",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    —
                                  </Typography>
                                ) : (
                                  ranges.map((r, i) => (
                                    <Typography
                                      key={`escort-${d.key}-r-${i}`}
                                      variant="caption"
                                      sx={{
                                        fontWeight: 800,
                                        color: "text.secondary",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: 240,
                                        textAlign: "right",
                                      }}
                                    >
                                      <TimeLabel minutes={r.startMin} /> -{" "}
                                      <TimeLabel minutes={r.endMin} />
                                    </Typography>
                                  ))
                                )}

                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 900,
                                      color:
                                        hours > 0
                                          ? "text.primary"
                                          : "text.disabled",
                                      whiteSpace: "nowrap",
                                      textAlign: "right",
                                    }}
                                  >
                                    {hours > 0
                                      ? `${formatHoursNumber(hours)}h`
                                      : "—"}
                                  </Typography>
                                  <Tooltip title="Clear time">
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEliteBlocksByDay((prev) => {
                                            const next = { ...prev };
                                            delete (next as any)[d.key];
                                            return next;
                                          });
                                        }}
                                        disabled={ranges.length === 0}
                                      >
                                        <ClearRoundedIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Box>
                              </Stack>
                            );
                          })()}
                        </TableCell>
                      ))}
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 700,
                          position: "sticky",
                          right: summaryColWidth * 2,
                          zIndex: 2,
                          bgcolor: tableSummaryBg,
                          width: summaryColWidth,
                          minWidth: summaryColWidth,
                          borderLeft: "1px solid",
                          borderLeftColor: "divider",
                        }}
                      >
                        {showEliteEscortJoke
                          ? formatCurrency(elite.daily)
                          : "—"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 700,
                          position: "sticky",
                          right: summaryColWidth,
                          zIndex: 2,
                          bgcolor: tableSummaryBg,
                          width: summaryColWidth,
                          minWidth: summaryColWidth,
                        }}
                      >
                        {showEliteEscortJoke
                          ? formatCurrency(elite.weekly)
                          : "—"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 700,
                          position: "sticky",
                          right: 0,
                          zIndex: 2,
                          bgcolor: tableSummaryBg,
                          width: summaryColWidth,
                          minWidth: summaryColWidth,
                        }}
                      >
                        {showEliteEscortJoke
                          ? formatCurrency(elite.annual)
                          : "—"}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              <Divider sx={{ my: 0.5 }} />

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Tooltip title="Add category">
                  <Button
                    size="small"
                    startIcon={<AddRoundedIcon />}
                    onClick={() => setAddCategoryOpen(true)}
                  >
                    Add Category
                  </Button>
                </Tooltip>
              </Box>
            </Stack>
          </Paper>
        )}

        {viewTab === "calendar" && (
          <Paper sx={panelSx}>
            <Stack spacing={1}>
              <Typography sx={{ fontWeight: 900 }}>Weekly Calendar</Typography>
              <Typography variant="body2" color="text.secondary">
                Blocks are stacked in category order.
              </Typography>

              {(() => {
                const startMin = scheduleMap.startMin;
                const endMin = scheduleMap.endMin;
                const windowMin = Math.max(1, endMin - startMin);
                const calendarHeight = 520;
                const pxPerMin = calendarHeight / windowMin;
                const hourMarks: number[] = [];
                const firstHour = Math.ceil(startMin / 60);
                const lastHour = Math.floor(endMin / 60);
                for (let h = firstHour; h <= lastHour; h += 1)
                  hourMarks.push(h);

                type CalBlock = {
                  key: string;
                  label: string;
                  subtitle?: string;
                  color: string;
                  startMin: number;
                  endMin: number;
                  top: number;
                  height: number;
                };

                const blocksByDay: Record<DayKey, CalBlock[]> = {
                  mon: [],
                  tue: [],
                  wed: [],
                  thu: [],
                  fri: [],
                  sat: [],
                  sun: [],
                };

                days.forEach((d) => {
                  categories.forEach((c) => {
                    const fixedBlocks = c.fixedBlocksByDay?.[d.key] || [];
                    if (fixedBlocks.length) {
                      fixedBlocks.forEach((b, idx) => {
                        const start = parseTimeToMinutes(b.start);
                        const end = parseTimeToMinutes(b.end);
                        if (start == null || end == null) return;
                        if (end <= start) return;
                        const color = getCategoryColor(c.id);
                        blocksByDay[d.key].push({
                          key: `${d.key}-${c.id}-fixed-${idx}`,
                          label: c.label,
                          subtitle: c.subtitle,
                          color,
                          startMin: start,
                          endMin: end,
                          top: (start - startMin) * pxPerMin,
                          height: Math.max(18, (end - start) * pxPerMin),
                        });
                      });
                      return;
                    }

                    const sched = scheduleMap.map[d.key][c.id];
                    if (!sched || sched.durationMin <= 0) return;
                    const start = sched.startMin;
                    const end = sched.endMin;
                    if (end <= start) return;
                    const color = getCategoryColor(c.id);
                    blocksByDay[d.key].push({
                      key: `${d.key}-${c.id}-${start}`,
                      label: c.label,
                      subtitle: c.subtitle,
                      color,
                      startMin: start,
                      endMin: end,
                      top: (start - startMin) * pxPerMin,
                      height: Math.max(18, (end - start) * pxPerMin),
                    });
                  });

                  if (showEliteEscortJoke) {
                    const eliteBlocks = eliteBlocksByDay[d.key] || [];
                    eliteBlocks.forEach((b) => {
                      const s = parseTimeToMinutes(b.start);
                      const e = parseTimeToMinutes(b.end);
                      if (s == null || e == null) return;
                      if (e <= s) return;
                      const color = getCategoryColor("elite-escort");
                      blocksByDay[d.key].push({
                        key: `${d.key}-elite-${s}`,
                        label: "Elite Escort Services",
                        color,
                        startMin: s,
                        endMin: e,
                        top: (s - startMin) * pxPerMin,
                        height: Math.max(18, (e - s) * pxPerMin),
                      });
                    });
                  }
                });

                const layoutByDay: Record<
                  DayKey,
                  { blocks: Array<CalBlock & { lane: number; lanes: number }> }
                > = {
                  mon: { blocks: [] },
                  tue: { blocks: [] },
                  wed: { blocks: [] },
                  thu: { blocks: [] },
                  fri: { blocks: [] },
                  sat: { blocks: [] },
                  sun: { blocks: [] },
                };

                // Avoid illegible overlap: only split into lanes within overlapping clusters.
                days.forEach((d) => {
                  const blocks = [...blocksByDay[d.key]].sort((a, b) => {
                    if (a.startMin !== b.startMin)
                      return a.startMin - b.startMin;
                    if (a.endMin !== b.endMin) return a.endMin - b.endMin;
                    return a.key.localeCompare(b.key);
                  });

                  const out: Array<CalBlock & { lane: number; lanes: number }> =
                    [];

                  const overlapAllowanceMin = (b: CalBlock) => {
                    const dur = Math.max(1, b.endMin - b.startMin);
                    return Math.floor(dur * 0.1);
                  };

                  const flushGroup = (group: CalBlock[]) => {
                    if (group.length === 0) return;
                    const laneEnds: number[] = [];
                    const temp: Array<CalBlock & { lane: number }> = [];
                    group.forEach((b) => {
                      const allowance = overlapAllowanceMin(b);
                      // Allow small overlap so near-collisions don't force a new lane.
                      let lane = laneEnds.findIndex(
                        (end) => end <= b.startMin + allowance
                      );
                      if (lane === -1) {
                        lane = laneEnds.length;
                        laneEnds.push(b.endMin);
                      } else {
                        laneEnds[lane] = b.endMin;
                      }
                      temp.push({ ...b, lane });
                    });
                    const lanes = Math.max(1, laneEnds.length);
                    temp.forEach((b) => out.push({ ...b, lanes }));
                  };

                  let group: CalBlock[] = [];
                  let groupMaxEnd = Number.NEGATIVE_INFINITY;
                  blocks.forEach((b) => {
                    if (group.length === 0) {
                      group = [b];
                      groupMaxEnd = b.endMin;
                      return;
                    }

                    // New cluster if it doesn't overlap the current cluster at all
                    // (with a small allowed overlap).
                    const allowance = overlapAllowanceMin(b);
                    if (b.startMin >= groupMaxEnd - allowance) {
                      flushGroup(group);
                      group = [b];
                      groupMaxEnd = b.endMin;
                      return;
                    }

                    group.push(b);
                    groupMaxEnd = Math.max(groupMaxEnd, b.endMin);
                  });
                  flushGroup(group);

                  layoutByDay[d.key] = { blocks: out };
                });

                const calendarGridSx = {
                  display: "grid",
                  gridTemplateColumns: "64px repeat(7, minmax(120px, 1fr))",
                  gap: 1,
                  alignItems: "start",
                } as const;

                const dayHeaderSx = {
                  fontWeight: 900,
                  fontSize: 12,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  textAlign: "center",
                } as const;

                const dayColumnSx = {
                  position: "relative",
                  height: calendarHeight,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: alpha(theme.palette.action.hover, 0.35),
                  overflow: "hidden",
                } as const;

                return (
                  <Box sx={calendarGridSx}>
                    <Box />
                    {days.map((d) => (
                      <Box key={`cal-head-${d.key}`} sx={dayHeaderSx}>
                        {d.label}
                      </Box>
                    ))}

                    <Box sx={{ position: "relative", height: calendarHeight }}>
                      {hourMarks.map((h) => {
                        const top = (h * 60 - startMin) * pxPerMin;
                        if (top < 0 || top > calendarHeight) return null;
                        const labelHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
                        const suffix = h >= 12 ? "PM" : "AM";
                        return (
                          <Box
                            key={`time-${h}`}
                            sx={{
                              position: "absolute",
                              top: top - 8,
                              right: 6,
                              fontSize: 11,
                              color: "text.secondary",
                              fontWeight: 700,
                            }}
                          >
                            <Box component="span">{labelHour}</Box>
                            <Box
                              component="sup"
                              sx={{
                                ml: 0.15,
                                fontSize: "0.68em",
                                fontWeight: 900,
                              }}
                            >
                              {suffix}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>

                    {days.map((d) => (
                      <Box key={`cal-col-${d.key}`} sx={dayColumnSx}>
                        {hourMarks.map((h) => {
                          const top = (h * 60 - startMin) * pxPerMin;
                          if (top < 0 || top > calendarHeight) return null;
                          return (
                            <Box
                              key={`line-${d.key}-${h}`}
                              sx={{
                                position: "absolute",
                                top,
                                left: 0,
                                right: 0,
                                borderTop: "1px solid",
                                borderTopColor: alpha(
                                  theme.palette.divider,
                                  isDarkMode ? 0.45 : 0.6
                                ),
                              }}
                            />
                          );
                        })}

                        {layoutByDay[d.key].blocks.map((b) => {
                          const lanes = b.lanes;
                          const baseWidthPct = 100 / lanes;
                          const overlapPct = lanes > 1 ? baseWidthPct * 0.1 : 0; // 10% overlap
                          const widthPct =
                            lanes > 1
                              ? baseWidthPct + overlapPct
                              : baseWidthPct;
                          let leftPct =
                            lanes > 1
                              ? b.lane * baseWidthPct - overlapPct / 2
                              : 0;
                          if (lanes > 1) {
                            if (b.lane === 0) leftPct = 0;
                            if (b.lane === lanes - 1) leftPct = 100 - widthPct;
                            leftPct = clampNumber(leftPct, 0, 100);
                          }

                          const maskImage =
                            lanes <= 1
                              ? undefined
                              : b.lane === 0
                                ? "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 88%, rgba(0,0,0,0) 100%)"
                                : b.lane === lanes - 1
                                  ? "linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 12%, rgba(0,0,0,1) 100%)"
                                  : "linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 12%, rgba(0,0,0,1) 88%, rgba(0,0,0,0) 100%)";
                          return (
                            <Box
                              key={b.key}
                              sx={{
                                position: "absolute",
                                top: b.top,
                                ...(lanes === 1
                                  ? { left: 0, right: 0 }
                                  : {
                                      left: `${leftPct}%`,
                                      width: `${widthPct}%`,
                                    }),
                                height: b.height,
                                minHeight: b.height,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: alpha(b.color, 0.85),
                                bgcolor: alpha(
                                  b.color,
                                  isDarkMode ? 0.22 : 0.18
                                ),
                                color: "text.primary",
                                boxSizing: "border-box",
                                px: 1,
                                display: "flex",
                                alignItems: "flex-start",
                                fontSize: lanes > 1 ? 11 : 12,
                                fontWeight: 800,
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                cursor: "default",
                                transition:
                                  "left 120ms ease, width 120ms ease, right 120ms ease, background-color 120ms ease, box-shadow 120ms ease",
                                ...(maskImage
                                  ? {
                                      WebkitMaskImage: maskImage,
                                      maskImage,
                                      WebkitMaskRepeat: "no-repeat",
                                      maskRepeat: "no-repeat",
                                    }
                                  : null),
                                "&:hover": {
                                  zIndex: 20,
                                  left: 0,
                                  right: 0,
                                  width: "auto",
                                  height: "auto",
                                  overflow: "visible",
                                  whiteSpace: "normal",
                                  bgcolor: alpha(
                                    b.color,
                                    isDarkMode ? 0.72 : 0.6
                                  ),
                                  boxShadow: 6,
                                  WebkitMaskImage: "none",
                                  maskImage: "none",
                                },
                                "&:hover .cal-block-text": {
                                  overflow: "visible",
                                  whiteSpace: "normal",
                                },
                                "&:hover .cal-block-label": {
                                  overflow: "visible",
                                  whiteSpace: "normal",
                                  textOverflow: "clip",
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: 99,
                                  bgcolor: alpha(b.color, 0.95),
                                  mr: 0.75,
                                  flex: "0 0 auto",
                                  mt: 0.75,
                                }}
                              />
                              <Box
                                className="cal-block-text"
                                sx={{
                                  minWidth: 0,
                                  overflow: "hidden",
                                  pt: 0.5,
                                  pb: 0.5,
                                }}
                              >
                                <Box
                                  className="cal-block-label"
                                  sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {b.label}
                                </Box>
                                {b.subtitle && b.height >= 34 && (
                                  <Box
                                    sx={{
                                      mt: 0.15,
                                      fontSize: 10,
                                      fontWeight: 700,
                                      color: alpha(
                                        theme.palette.text.primary,
                                        0.65
                                      ),
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {b.subtitle}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ))}
                  </Box>
                );
              })()}
            </Stack>
          </Paper>
        )}

        <Paper sx={panelSx}>
          <Stack spacing={1}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }} />
                  <TableCell sx={{ fontWeight: 800 }} align="right">
                    Grand Total{" "}
                    <Box
                      component="span"
                      sx={{
                        ml: 0.75,
                        fontWeight: 900,
                        color: alpha(theme.palette.text.secondary, 0.95),
                        fontSize: "0.85em",
                      }}
                    >
                      (x{totals.metersMultiplier.toFixed(2)} from meters)
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 800 }}>Daily</TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 900, color: "success.main" }}
                  >
                    {formatCurrency(totals.weeklyTotal / 7)}
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 800 }}>Weekly</TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 900, color: "success.main" }}
                  >
                    {formatCurrency(totals.weeklyTotal)}
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 800 }}>Annually</TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 900, color: "success.main" }}
                  >
                    {formatCurrency(totals.annualTotal)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Stack>
        </Paper>
      </Stack>

      <Dialog
        open={meterDialogOpen}
        onClose={() => setMeterDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 900 }}>{meterDialogTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Paper
              sx={{
                p: 1.25,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: alpha(theme.palette.action.hover, 0.35),
              }}
            >
              <Stack spacing={0.75}>
                <Stack
                  direction="row"
                  alignItems="baseline"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Typography variant="body2" sx={{ fontWeight: 900 }}>
                    {meterDialogTitle}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 800 }}
                  >
                    {meterDialogTotals.earned}/{meterDialogTotals.max} (
                    {meterDialogTotals.tier}
                    {meterDialogTotals.bonusPct > 0
                      ? ` +${meterDialogTotals.bonusPct}%`
                      : ""}
                    )
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={meterDialogTotals.pct * 100}
                  sx={{
                    height: 10,
                    borderRadius: 10,
                    bgcolor: alpha(theme.palette.common.white, 0.06),
                    "& .MuiLinearProgress-bar": { borderRadius: 10 },
                  }}
                />
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{
                    fontSize: 12,
                    color: alpha(theme.palette.text.secondary, 0.9),
                    fontWeight: 900,
                  }}
                >
                  <Box>Low</Box>
                  <Box>Medium</Box>
                  <Box>High</Box>
                </Stack>
              </Stack>
            </Paper>

            <Stack spacing={2}>
              {meterSections.map((section) => {
                const items = meterState.items[meterDialogKind][section];
                return (
                  <Paper
                    key={section}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setMeterDragOver(section);
                    }}
                    onDragLeave={() => {
                      setMeterDragOver((prev) =>
                        prev === section ? null : prev
                      );
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const payload = meterDrag;
                      setMeterDragOver(null);
                      setMeterDrag(null);
                      if (!payload) return;
                      moveMeterItem(payload.from, section, payload.itemId);
                    }}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor:
                        meterDragOver === section ? "primary.main" : "divider",
                      bgcolor: alpha(theme.palette.background.paper, 0.85),
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography sx={{ fontWeight: 900 }}>
                          {section}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 800 }}
                        >
                          Points:{" "}
                          {items.reduce(
                            (sum, i) =>
                              sum +
                              (meterState.checked[i.id]
                                ? Number(i.points) || 0
                                : 0),
                            0
                          )}
                          /{" "}
                          {items.reduce(
                            (sum, i) => sum + (Number(i.points) || 0),
                            0
                          )}
                        </Typography>
                      </Stack>

                      <Stack spacing={0.5}>
                        {items.map((item) => (
                          <Stack
                            key={item.id}
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{
                              p: 0.75,
                              borderRadius: 1.5,
                              bgcolor: alpha(theme.palette.action.hover, 0.25),
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ minWidth: 0 }}
                            >
                              <Checkbox
                                checked={Boolean(meterState.checked[item.id])}
                                onChange={() => toggleMeterItem(item.id)}
                              />
                              <Box
                                draggable
                                onDragStart={(e) => {
                                  e.dataTransfer.setData("text/plain", item.id);
                                  e.dataTransfer.effectAllowed = "move";
                                  setMeterDrag({
                                    from: section,
                                    itemId: item.id,
                                  });
                                }}
                                onDragEnd={() => {
                                  setMeterDrag(null);
                                  setMeterDragOver(null);
                                }}
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  cursor: "grab",
                                  color: "text.disabled",
                                  "&:active": { cursor: "grabbing" },
                                }}
                                aria-label="Drag to move item"
                                title="Drag to move item"
                              >
                                <DragIndicatorRoundedIcon fontSize="small" />
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700 }}
                              >
                                {item.label}
                              </Typography>
                            </Stack>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <Chip
                                size="small"
                                label={`${item.points >= 0 ? "+" : ""}${item.points}`}
                                sx={{
                                  fontWeight: 900,
                                  bgcolor: alpha(
                                    theme.palette.action.hover,
                                    0.6
                                  ),
                                }}
                              />
                              <Tooltip title="Delete item">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    deleteMeterItem(section, item.id)
                                  }
                                  sx={{ color: "text.secondary" }}
                                >
                                  <DeleteRoundedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Stack>
                        ))}
                      </Stack>

                      <Divider sx={{ my: 0.5 }} />

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ xs: "stretch", sm: "center" }}
                      >
                        <TextField
                          size="small"
                          placeholder="Add new item..."
                          value={meterAddLabelBySection[section]}
                          onChange={(e) =>
                            setMeterAddLabelBySection((prev) => ({
                              ...prev,
                              [section]: e.target.value,
                            }))
                          }
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          size="small"
                          value={meterAddPointsBySection[section]}
                          onChange={(e) =>
                            setMeterAddPointsBySection((prev) => ({
                              ...prev,
                              [section]: e.target.value,
                            }))
                          }
                          inputProps={{ inputMode: "numeric" }}
                          sx={{
                            width: 110,
                            "& input": { textAlign: "right" },
                          }}
                        />
                        <Tooltip title="Add item">
                          <span>
                            <IconButton
                              onClick={() => addMeterItem(section)}
                              color="primary"
                            >
                              <AddRoundedIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMeterDialogOpen(false)} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addCategoryOpen} onClose={() => setAddCategoryOpen(false)}>
        <DialogTitle>Add Category</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25} sx={{ pt: 1, minWidth: 360 }}>
            <TextField
              label="Name"
              size="small"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              autoFocus
            />
            <TextField
              label="Rate"
              size="small"
              value={newCategoryRate}
              onChange={(e) =>
                setNewCategoryRate(
                  clampNumber(Number(e.target.value || 0), 0, 5000)
                )
              }
              inputProps={{ inputMode: "decimal" }}
              sx={{ "& input": { textAlign: "right" } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCategoryOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddCategory}
            disabled={!newCategoryName.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <PageShareModal
        open={pageShareOpen}
        onClose={() => setPageShareOpen(false)}
        pageType="wife"
        payload={sharePayload}
        label="Spouse Value Calculator"
        buildShareUrl={(shareId) =>
          `${window.location.origin}/shared/spouse-value-calculator?shareId=${shareId}`
        }
      />

      <Dialog open={Boolean(fixedEdit)} onClose={() => setFixedEdit(null)}>
        <DialogTitle>Edit Fixed Blocks</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25} sx={{ pt: 1, minWidth: 420 }}>
            {(fixedEdit?.blocks || []).map((b, idx) => (
              <Stack
                key={`block-${idx}`}
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <TextField
                  label="Start"
                  size="small"
                  type="time"
                  value={b.start}
                  onChange={(e) => {
                    if (!fixedEdit) return;
                    const next = [...fixedEdit.blocks];
                    next[idx] = { ...next[idx], start: e.target.value };
                    setFixedEdit({ ...fixedEdit, blocks: next });
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 150 }}
                />
                <TextField
                  label="End"
                  size="small"
                  type="time"
                  value={b.end}
                  onChange={(e) => {
                    if (!fixedEdit) return;
                    const next = [...fixedEdit.blocks];
                    next[idx] = { ...next[idx], end: e.target.value };
                    setFixedEdit({ ...fixedEdit, blocks: next });
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 150 }}
                />
                <Tooltip title="Remove block">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (!fixedEdit) return;
                        const next = fixedEdit.blocks.filter(
                          (_, i) => i !== idx
                        );
                        setFixedEdit({ ...fixedEdit, blocks: next });
                      }}
                      disabled={(fixedEdit?.blocks || []).length <= 1}
                    >
                      <DeleteRoundedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            ))}

            <Button
              size="small"
              startIcon={<AddRoundedIcon />}
              onClick={() => {
                if (!fixedEdit) return;
                setFixedEdit({
                  ...fixedEdit,
                  blocks: [
                    ...fixedEdit.blocks,
                    { start: "12:00", end: "12:30" },
                  ],
                });
              }}
            >
              Add Block
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFixedEdit(null)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!fixedEdit) return;
              const blocks = fixedEdit.blocks
                .map((b) => ({ start: b.start, end: b.end }))
                .filter((b) => {
                  const s = parseTimeToMinutes(b.start);
                  const e = parseTimeToMinutes(b.end);
                  return s != null && e != null && e > s;
                });
              const hours = computeHoursFromBlocks(blocks);
              setCategories((prev) =>
                prev.map((row) => {
                  if (row.id !== fixedEdit.categoryId) return row;
                  return {
                    ...row,
                    fixedBlocksByDay: {
                      ...(row.fixedBlocksByDay || {}),
                      [fixedEdit.dayKey]: blocks,
                    },
                    hoursByDay: {
                      ...row.hoursByDay,
                      [fixedEdit.dayKey]: hours,
                    },
                  };
                })
              );
              setFixedEdit(null);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpouseValueCalculator;
